import { useEffect, useMemo, useRef, useState } from 'react'

interface LinieComparata {
  vendorName: string | null
  amountTotal: number
  docDate: string | null
  vendorCif: string | null
}

/** Cele două citiri ale aceluiași fișier, puse față în față. */
interface Comparatie {
  fileId: number
  fileName: string
  model: string | null
  curent: LinieComparata[]
  propus: LinieComparata[]
  totalCurent: number
  totalPropus: number
  pierdeBonuri: boolean
}
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { AntetFix } from '../../components/ui/AntetFix'
import { Field } from '../../components/ui/Field'
import { PLAN_CONTURI } from '../../lib/planConturi'
import {
  NotaRowCells,
  NotaHeaderEA,
  type NotaContabila,
} from '../../components/deconturi/NoteContabileShared'
import { formatDocns } from '../../lib/decontFormat'
import { ChevronRight } from 'lucide-react'
import { lunaDinZiISO, lunaInCuvinte, luniRecente } from '../../lib/perioade'
import { useAuthStore } from '../../stores/auth'
import { randeazaPaginaPdf } from '../../lib/pdfPagina'
import { AddClientModal, type PrecompletarePartener } from '../../components/orders/AddClientModal'

type LineKind = 'bon' | 'diurna' | 'mileage' | 'factura_directa'
type DecontStatus = 'new' | 'extracted' | 'pending_review' | 'verified' | 'exported'

interface DecontLine {
  id: number
  kind: LineKind
  ordinal: number
  vendorName: string | null
  docRef: string | null
  docDate: string | null
  amountTotal: string
  descriereDetaliata: string
  categoryId: number | null
  category: { id: number; code: string; label: string; accountCode: string } | null
  costCenterId: number | null
  costCenter: { id: number; code: string; name: string } | null
  accountCode: string | null
  vendorCif: string | null
  cumparatorCif: string | null
  tvaDeductibil: boolean
  amountNet: string | null
  amountVat: string | null
  vatRate: string | null
  amountNet2: string | null
  amountVat2: string | null
  vatRate2: string | null
  nrZile: number | null
  rataNeimpozabila: string | null
  km: number | null
  consumPer100km: string | null
  pretLitru: string | null
  ocrConfidence: string | null
  needsReview: boolean
  /** Verificari concrete (problemeBon.ts pe server): ce anume nu e in regula la bon. */
  probleme?: {
    cod: 'total' | 'sume' | 'data' | 'cif' | 'dublura'
    mesaj: string
    pentruAngajat: boolean
  }[]
  /** Angajatul a confirmat sau a corectat citirea, cu bonul in mana. */
  confirmatAngajatAt?: string | null
  sourceFileId: number | null
  /** Pagina din fisier (de la 1) pe care OCR-ul a gasit randul — la PDF-urile cu mai multe bonuri. */
  sourcePageNum?: number | null
  eaPartnerName: string | null
  eaMatchStatus: 'pending' | 'matched' | 'name_mismatch' | 'cif_not_found' | 'no_cif'
}

interface DecontFile {
  id: number
  fileName: string
  mimeType: string
  kind: 'bonuri_scan' | 'foaie_parcurs' | 'other'
  ocrStatus: 'pending' | 'running' | 'done' | 'failed' | 'skipped'
  ocrError: string | null
  ocrCost: { costUsd: number; calls: number } | null
  /** Cand exista, fisierul are o recitire pusa deoparte, care asteapta confirmarea. */
  propunereLaData: string | null
  propunereModel: string | null
  /** „Ce reprezintă cheltuiala?" — scrisa de angajat pe „Decontul meu". */
  descriereAngajat?: string | null
  /** De ce e bonul pe luna asta, cand a ajuns aici din alta. */
  notaLuna: string | null
  /** Luna in care a fost incarcat, daca a fost mutat de atunci. */
  perioadaInitiala: string | null
}

interface Decont {
  id: number
  period: string
  status: DecontStatus
  currency: 'LEI' | 'EUR'
  currencyRate: string | null
  avansPrimit: string
  totalCheltuieli: string | null
  totalFacturaDirecta: string | null
  balance: string | null
  balanceDirection: 'zero' | 'de_restituit_angajat' | 'de_incasat_angajat'
  numarDecont: number | null
  employee: { id: number; fullName: string; eaAccountCode: string; eaAccountCodeEur: string | null }
  ordinDeplasare: { id: number; numar: number | null; destinatie: string } | null
  files: DecontFile[]
  lines: DecontLine[]
}

interface ExpenseCategory {
  id: number
  code: string
  label: string
  accountCode: string
  defaultCostCenterId: number | null
}

interface CostCenter {
  id: number
  code: string
  name: string
  department: string | null
}

const KIND_LABEL: Record<LineKind, string> = {
  bon: 'Bon',
  diurna: 'Diurnă',
  mileage: 'Km auto',
  factura_directa: 'Factură directă',
}

/*
  Eticheta bonului vine din verificari concrete (problemeBon.ts), nu din scorul OCR-ului.

  Pana in noiembrie 2026 aici era increderea pe care modelul si-o da singur: la bonurile
  mototolite ale lui Doru iesea „Citire nesigura” pe fiecare rand, desi sumele erau corecte.
  Acum rosu = o problema aratata (suma, data, CIF) pe care angajatul n-a confirmat-o; galben = a
  confirmat-o, deci ramane doar o privire; verde = totul se leaga. Scorul OCR ramane in tooltip.
*/
type StareBon = 'ok' | 'confirmat' | 'problema'

function stareBon(line: DecontLine): StareBon | null {
  if (line.kind !== 'bon' && line.kind !== 'factura_directa') return null
  const p = line.probleme ?? []
  if (p.length === 0) return 'ok'
  return line.confirmatAngajatAt ? 'confirmat' : 'problema'
}

const ETICHETA_PROBLEMA: Record<string, string> = {
  total: 'Total necitit',
  sume: 'Sume de verificat',
  data: 'Dată de verificat',
  cif: 'CUI de verificat',
  dublura: 'Posibil deja decontat',
}

function confidenceClass(line: DecontLine): string {
  const st = stareBon(line)
  if (st == null) return ''
  if (st === 'ok') return 'border-l-4 border-l-green-500'
  if (st === 'confirmat') return 'border-l-4 border-l-amber-500'
  return 'border-l-4 border-l-red-500'
}

/** Eticheta (bulină colorată + text): ce anume nu e în regulă la bon, sau că totul se leagă. */
function confidencePill(
  line: DecontLine,
): { text: string; cls: string; dot: string; title: string } | null {
  const st = stareBon(line)
  if (st == null) return null
  const p = line.probleme ?? []
  const scor = line.ocrConfidence ? ` · scor citire ${Number(line.ocrConfidence).toFixed(2)}` : ''
  const lista = p.map((x) => x.mesaj).join(' ')
  if (st === 'ok')
    return {
      text: 'Se leagă',
      cls: 'bg-green-100 text-green-700',
      dot: 'bg-green-500',
      title: `Suma, data și codul fiscal trec verificările${scor}`,
    }
  if (st === 'confirmat')
    return {
      text: 'Confirmat de angajat',
      cls: 'bg-amber-100 text-amber-700',
      dot: 'bg-amber-500',
      title: `Angajatul a verificat pe bon. ${lista}${scor}`,
    }
  const nume = [...new Set(p.map((x) => ETICHETA_PROBLEMA[x.cod] ?? x.cod))].join(' · ')
  return {
    text: nume,
    cls: 'bg-red-100 text-red-700',
    dot: 'bg-red-500',
    title: `${lista}${scor}`,
  }
}

function fmt(n: string | number | null | undefined): string {
  if (n == null) return '—'
  return Number(n).toLocaleString('ro-RO', { minimumFractionDigits: 2 })
}

/**
 * Afiseaza un toast adecvat dupa o operatie de re-OCR. Daca raspunsul indica
 * ca API-ul Anthropic a returnat eroarea de plafon de cheltuieli atins, afiseaza
 * un mesaj clar cu instructiuni — altfel, mesaj generic.
 */
function showReocrToast(
  file: { ocrStatus?: string; ocrError?: string | null } | undefined,
  successMsg: string,
): void {
  if (file?.ocrStatus === 'failed') {
    const err = file.ocrError ?? ''
    if (/usage limits|spend limit|rate.?limit/i.test(err)) {
      const dateMatch = err.match(/regain access on ([\d-]+)/i)
      const when = dateMatch ? ` (acces revine pe ${dateMatch[1]})` : ''
      toast.error(
        `Limita de cheltuieli pe API-ul Anthropic a fost atinsă${when}. Mărește plafonul la console.anthropic.com → Settings → Usage Limits, sau așteaptă resetul automat.`,
        { duration: 12000 },
      )
    } else {
      toast.error(`Citirea a eșuat: ${err.slice(0, 200) || 'eroare necunoscută'}`)
    }
  } else {
    toast.success(successMsg)
  }
}

export function DeconturiEditorPage() {
  const { id } = useParams<{ id: string }>()
  const decontId = Number(id)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [editLine, setEditLine] = useState<DecontLine | null>(null)
  /*
    Furnizorul unei facturi directe negăsit în EA: se creează din fereastra comună de furnizori
    (completată din OCR și ANAF), pleacă în EA, apoi se reface identificarea. Înainte, cineva îl
    crea de mână în EA și apăsa „reidentifică".
  */
  const [furnizorNou, setFurnizorNou] = useState<PrecompletarePartener | null>(null)
  const [addKind, setAddKind] = useState<LineKind | null>(null)
  const [viewFileId, setViewFileId] = useState<number | null>(null)
  // Raportul decontului (formularul PDF) deschis in pagina, fara descarcare.
  const [raportDeschis, setRaportDeschis] = useState(false)
  const [overlayFileId, setOverlayFileId] = useState<number | null>(null)
  /*
    Randul pe care s-a apasat „Vezi".

    Pana acum nu se retinea niciunul, iar cand un PDF avea opt bonuri se aprindeau toate opt la
    fel de tare — arata ca si cum s-ar fi selectat tot. Cu randul apasat retinut, el primeste
    evidentierea tare, iar fratii lui din acelasi document doar o urma de fundal.
  */
  const [randApasatId, setRandApasatId] = useState<number | null>(null)
  const [tab, setTab] = useState<'randuri' | 'note'>('randuri')
  const [detaliiLine, setDetaliiLine] = useState<DecontLine | null>(null)
  // Ecranul arata cum arata nota contabila, nu bonul. Cine isi incarca bonurile n-are ce
  // face cu 4426 si cu template-ul de import.
  const eAdmin = useAuthStore((st) => st.user?.role === 'admin')

  // "Vezi" pe un rand: populeaza panoul din dreapta (desktop) si, pe ecrane
  // inguste unde panoul e stivuit jos, deschide direct overlay-ul full-screen.
  const viewFile = (fileId: number, lineId?: number) => {
    setViewFileId(fileId)
    setRandApasatId(lineId ?? null)
    if (window.matchMedia('(max-width: 1023px)').matches) setOverlayFileId(fileId)
  }

  const query = useQuery<{ decont: Decont; ocrCostTotal: number }>({
    queryKey: ['decont', decontId],
    queryFn: async () =>
      (await api.get<{ decont: Decont; ocrCostTotal: number }>(`/deconturi/${decontId}`)).data,
    // Cat timp exista linii neidentificate inca (procesare automata in fundal),
    // reincarca periodic pana se finalizeaza.
    refetchInterval: (q) => {
      const d = q.state.data?.decont
      const lines = d?.lines ?? []
      const pendingMatch = lines.some(
        (l) => (l.kind === 'bon' || l.kind === 'factura_directa') && l.eaMatchStatus === 'pending',
      )
      // Reincarca si cat timp un fisier inca se proceseaza OCR (apare un fisier
      // nou scanat / se re-ruleaza), ca panoul de fisiere sa se actualizeze singur.
      const ocrInFlight = (d?.files ?? []).some(
        (f) => f.ocrStatus === 'pending' || f.ocrStatus === 'running',
      )
      return pendingMatch || ocrInFlight ? 8000 : false
    },
  })
  const catQuery = useQuery<{ categories: ExpenseCategory[] }>({
    queryKey: ['expense-categories'],
    queryFn: async () =>
      (await api.get<{ categories: ExpenseCategory[] }>('/expense-categories')).data,
  })

  const decont = query.data?.decont

  /*
    Randul selectat (apasat, editat sau deschis in detalii) aduce in previzualizare fisierul si
    PAGINA lui. Gabriela urca un singur PDF cu 7 bonuri: pana acum previzualizarea ramanea pe
    pagina 1 orice rand ai fi ales (Alexandru, 02.11.2026).
  */
  const randActivId = editLine?.id ?? detaliiLine?.id ?? randApasatId
  const randActiv = decont?.lines.find((l) => l.id === randActivId) ?? null
  useEffect(() => {
    if (randActiv?.sourceFileId) setViewFileId(randActiv.sourceFileId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [randActiv?.id])
  const paginaRandActiv =
    randActiv && randActiv.sourceFileId === viewFileId ? (randActiv.sourcePageNum ?? null) : null
  const categories = useMemo(() => catQuery.data?.categories ?? [], [catQuery.data])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['decont', decontId] })
    queryClient.invalidateQueries({ queryKey: ['deconturi'] })
    queryClient.invalidateQueries({ queryKey: ['cross-check', decontId] })
  }

  const patchDecont = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await api.patch(`/deconturi/${decontId}`, body)).data,
    onSuccess: () => {
      invalidate()
      toast.success('Salvat')
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const deleteLine = useMutation({
    mutationFn: async (lineId: number) => (await api.delete(`/deconturi/lines/${lineId}`)).data,
    onSuccess: () => {
      invalidate()
      toast.success('Linie ștearsă')
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const exportDecont = useMutation({
    mutationFn: async () => (await api.post(`/deconturi/${decontId}/export`)).data,
    onSuccess: () => {
      invalidate()
      toast.success('Decont exportat')
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const reocrAll = useMutation({
    mutationFn: async (fileIds: number[]) => {
      const results: { ocrStatus: string; ocrError: string | null }[] = []
      for (const id of fileIds) {
        const r = await api.post<{ file: { ocrStatus: string; ocrError: string | null } }>(
          `/deconturi/files/${id}/reocr?aplica=1`,
        )
        results.push(r.data.file)
      }
      return results
    },
    onSuccess: (results) => {
      invalidate()
      const limitHit = results.find(
        (r) => r.ocrStatus === 'failed' && /usage limits|spend limit/i.test(r.ocrError ?? ''),
      )
      if (limitHit) {
        showReocrToast(limitHit, 'Citirea a fost reluată')
      } else {
        const okCount = results.filter((r) => r.ocrStatus === 'done').length
        toast.success(`Citire reluată — ${okCount}/${results.length} OK`)
      }
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  /**
   * Mutarea unui bon pe alta luna.
   *
   * `docd`-ul care pleaca in ExpertAccounts e ultima zi a lunii DECONTULUI, nu data de pe bon.
   * Deci cand un bon a ajuns pe luna gresita (de regula fiindca OCR-ul a citit alt an, iar
   * reincadrarea automata l-a crezut), corectarea datei pe linie nu schimba nimic in export —
   * trebuie mutat bonul.
   */
  /*
    Bonuri a caror DATA nu se potriveste cu luna decontului.

    Reincadrarea automata ruleaza dupa OCR (`reincadrare.service.ts`). Cand OCR-ul esueaza si
    data se scrie cu mana, nimic nu o mai verifica: decontul ramane pe luna gresita, iar omul
    vede data corectata si crede ca s-a rezolvat. Exact asta s-a intamplat cu bonul CARWASH,
    citit 2025 in loc de 2026 (Alexandru, 19.09.2026).

    NU se muta singur. Mutarea tacuta a fost incercata si a costat scump — vezi comentariul din
    `reincadrare.service.ts`: oamenii isi gaseau bonurile disparute fara sa stie de ce. Aici se
    SPUNE, iar mutarea ramane un buton.
  */
  const bonuriPeAltaLuna = useMemo(() => {
    if (!decont) return []
    const peFisier = new Map<number, { luna: string; docDate: string; nume: string }>()
    for (const l of decont.lines) {
      if (!l.docDate || l.sourceFileId == null) continue
      const luna = lunaDinZiISO(l.docDate)
      if (!luna || luna === decont.period) continue
      if (peFisier.has(l.sourceFileId)) continue
      const f = decont.files.find((x) => x.id === l.sourceFileId)
      peFisier.set(l.sourceFileId, {
        luna,
        docDate: l.docDate,
        nume: f?.fileName ?? l.vendorName ?? 'bon',
      })
    }
    return [...peFisier.entries()].map(([fileId, v]) => ({ fileId, ...v }))
  }, [decont])

  /*
    Grupate pe luna in care ar trebui sa plece. Aproape mereu e una singura — sase bonuri din
    iulie ramase intr-un decont pe august —, si atunci sase butoane identice n-au niciun rost.
  */
  const lunaTintaUnica =
    bonuriPeAltaLuna.length > 0 &&
    bonuriPeAltaLuna.every((b) => b.luna === bonuriPeAltaLuna[0].luna)
      ? bonuriPeAltaLuna[0].luna
      : null

  // Pliat implicit: cu sase bonuri, lista acoperea jumatate de ecran (Alexandru, 19.09.2026).
  const [nepotrivireDeschisa, setNepotrivireDeschisa] = useState(false)

  const [mutaFileId, setMutaFileId] = useState<number | null>(null)
  const [lunaTinta, setLunaTinta] = useState('')
  const mutaFisier = useMutation({
    mutationFn: async (v: { fileId: number; period: string }) =>
      (
        await api.post<{ decontId: number; decontSursaSters: boolean }>(
          `/deconturi/files/${v.fileId}/muta`,
          { period: v.period },
        )
      ).data,
    onSuccess: (data, v) => {
      setMutaFileId(null)
      invalidate()
      toast.success(`Bon mutat pe ${lunaInCuvinte(v.period)}`)
      // Bonul nu mai e aici — deschidem decontul unde a plecat, altfel omul ramane pe o
      // pagina din care tocmai a disparut ce se uita.
      navigate(`/hr/deconturi/${data.decontId}`)
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const cereRefacere = useMutation({
    mutationFn: async (v: { fileId: number; motiv: string | null }) =>
      (await api.post(`/deconturi/files/${v.fileId}/cere-refacere`, { motiv: v.motiv })).data,
    onSuccess: (_d, v) => {
      invalidate()
      toast.success(v.motiv ? 'Angajatul vede cererea pe ecranul lui.' : 'Cerere anulată.')
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const stergeBon = useMutation({
    mutationFn: async (fileId: number) => (await api.delete(`/deconturi/files/${fileId}`)).data,
    onSuccess: () => {
      setViewFileId(null)
      invalidate()
      toast.success('Bon șters. Poza a mers în „_sterse" și se poate recupera.')
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  /**
   * Mutarea TUTUROR bonurilor nepotrivite, pe aceeasi luna.
   *
   * Secvential, nu in paralel: fiecare mutare poate sterge decontul sursa ramas gol, iar doua
   * cereri simultane pe acelasi decont s-ar calca. Navigarea se face o singura data, la final.
   */
  const mutaToate = useMutation({
    mutationFn: async (v: { fileIds: number[]; period: string }) => {
      let ultimulDecont = decontId
      for (const fileId of v.fileIds) {
        const r = await api.post<{ decontId: number }>(`/deconturi/files/${fileId}/muta`, {
          period: v.period,
        })
        ultimulDecont = r.data.decontId
      }
      return { decontId: ultimulDecont, cate: v.fileIds.length }
    },
    onSuccess: (r, v) => {
      invalidate()
      toast.success(
        `${r.cate} ${r.cate === 1 ? 'bon mutat' : 'bonuri mutate'} pe ${lunaInCuvinte(v.period)}`,
      )
      navigate(`/hr/deconturi/${r.decontId}`)
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  /**
   * Recitirea cu Sonnet NU mai inlocuieste liniile.
   *
   * Acelasi model, pe acelasi PDF cu trei bonuri, a gasit odata 3 si a doua oara 1 — iar
   * recitirea le stergea pe primele definitiv. Acum se cere confirmarea, cu cele doua citiri
   * una langa alta.
   */
  const [comparatie, setComparatie] = useState<Comparatie | null>(null)

  const reocrSonnet = useMutation({
    mutationFn: async (fileId: number) =>
      (
        await api.post<{ aplicat: boolean; comparatie: Comparatie | null }>(
          `/deconturi/files/${fileId}/reocr?model=sonnet`,
          undefined,
          { timeout: 240_000 },
        )
      ).data,
    onSuccess: (data) => {
      if (data.comparatie) setComparatie(data.comparatie)
      else toast.info('Recitirea n-a întors nimic de comparat.')
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  /*
    Deschide o recitire care sta deja deoparte.

    Pana acum comparatia se vedea DOAR ca raspuns la apasarea butonului „Sonnet". O recitire
    facuta altfel — din workflow-ul de recitire pe decont intreg — ramanea in baza fara nicio
    cale de a ajunge la ea: singura iesire era sa platesti inca o citire.
  */
  const deschidePropunerea = useMutation({
    mutationFn: async (fileId: number) =>
      (await api.get<{ comparatie: Comparatie | null }>(`/deconturi/files/${fileId}/propunere`))
        .data,
    onSuccess: (data) => {
      if (data.comparatie) setComparatie(data.comparatie)
      else toast.info('Recitirea nu mai e disponibilă pentru acest fișier.')
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const acceptaPropunerea = useMutation({
    mutationFn: async (fileId: number) =>
      (await api.post(`/deconturi/files/${fileId}/propunere/accepta`)).data,
    onSuccess: () => {
      setComparatie(null)
      invalidate()
      toast.success('Citirea nouă a înlocuit-o pe cea veche.')
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const renuntaLaPropunere = useMutation({
    mutationFn: async (fileId: number) =>
      (await api.post(`/deconturi/files/${fileId}/propunere/renunta`)).data,
    onSuccess: () => {
      setComparatie(null)
      toast.info('Am păstrat citirea de dinainte.')
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const matchPartners = useMutation({
    mutationFn: async () => {
      const r = await api.post<{
        result: {
          status: string
          message?: string
          matched: number
          nameMismatch: number
          cifNotFound: number
          noCif: number
        }
      }>(`/deconturi/${decontId}/match-partners`, undefined, { timeout: 90_000 })
      return r.data.result
    },
    onSuccess: (r) => {
      invalidate()
      if (r.status === 'ea_unavailable') {
        toast.error(r.message ?? 'ExpertAccounts indisponibil')
      } else {
        toast.success(
          `Parteneri EA: ${r.matched} ok` +
            (r.nameMismatch > 0 ? `, ${r.nameMismatch} nume diferă` : '') +
            (r.cifNotFound > 0 ? `, ${r.cifNotFound} CIF negăsit` : ''),
        )
      }
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const reocrCif = useMutation({
    mutationFn: async () => {
      const r = await api.post<{
        result: {
          status: string
          message?: string
          filesProcessed: number
          linesChecked: number
          cifFilled: number
          stillMissing: number
        }
      }>(`/deconturi/${decontId}/reocr-cif`, undefined, { timeout: 180_000 })
      return r.data.result
    },
    onSuccess: (r) => {
      invalidate()
      if (r.status === 'nextcloud_unavailable' || r.status === 'no_files') {
        toast.error(r.message ?? 'Re-citire indisponibilă')
      } else if (r.status === 'no_lines') {
        toast.success('Toate bonurile au deja cod fiscal')
      } else {
        toast.success(
          `CIF re-citit: ${r.cifFilled} completate` +
            (r.stillMissing > 0 ? `, ${r.stillMissing} încă lipsă` : ''),
        )
      }
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const dedupe = useMutation({
    mutationFn: async () => {
      const r = await api.post<{ result: { removed: number } }>(`/deconturi/${decontId}/dedupe`)
      return r.data.result
    },
    onSuccess: (r) => {
      invalidate()
      toast.success(
        r.removed > 0
          ? `${r.removed} ${r.removed === 1 ? 'duplicat eliminat' : 'dubluri eliminate'}`
          : 'Niciun duplicat găsit',
      )
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const rescan = useMutation({
    mutationFn: async () =>
      (await api.post(`/deconturi/${decontId}/reprocess`, undefined, { timeout: 120_000 })).data,
    onSuccess: () => {
      invalidate()
      toast.success('Re-scanare + procesare făcută')
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  if (query.isLoading) {
    return (
      <Card>
        <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
      </Card>
    )
  }
  if (!decont) {
    return (
      <Card>
        <p className="text-sm text-b4l-text-muted">Decont inexistent.</p>
      </Card>
    )
  }

  const lines = [...decont.lines].sort((a, b) => a.ordinal - b.ordinal)
  const bonuri = lines.filter((l) => l.kind === 'bon')
  const speciale = lines.filter((l) => l.kind === 'diurna' || l.kind === 'mileage')
  const facturi = lines.filter((l) => l.kind === 'factura_directa')
  const explicatii = new Map<number, string>()
  for (const f of decont.files) {
    const e = f.descriereAngajat?.trim()
    if (e) explicatii.set(f.id, e)
  }
  const canEdit = decont.status !== 'exported'
  const balanceLabel =
    decont.balanceDirection === 'de_incasat_angajat'
      ? 'Diferență de încasat'
      : 'Diferență de restituit'

  return (
    /*
      Pe ecran larg pagina NU deruleaza: umple inaltimea lui `<main>`, iar cele doua panouri
      deruleaza fiecare pe cont propriu. Altfel, ca sa compari randul 9 cu bonul lui, derulai
      lista si pierdeai poza, sau invers — „sunt greu de urmarit".

      Sub `lg` raman stivuite si deruleaza pagina normal: acolo doua zone de derulare una sub
      alta ar fi mai rau decat una singura.
    */
    <div className="space-y-4 lg:flex lg:h-full lg:flex-col lg:gap-4 lg:space-y-0">
      {raportDeschis && (
        <RaportDecont
          decontId={decont.id}
          titlu={`Decont ${decont.employee.fullName} — ${decont.period}`}
          onClose={() => setRaportDeschis(false)}
        />
      )}
      <AntetFix>
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/hr/deconturi')}
              className="text-xs text-b4l-text-muted hover:underline"
            >
              ← Toate deconturile
            </button>
            <h1 className="text-xl font-semibold">
              {decont.employee.fullName} — {decont.period}
              {(() => {
                const d = formatDocns(decont.employee.fullName, decont.period, decont.numarDecont)
                return d ? ` · ${d}` : ''
              })()}
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setRaportDeschis(true)}>Vizualizează decont</Button>
            <Button variant="secondary" onClick={() => descarcaDecont(decont.id, 'pdf')}>
              Decont PDF
            </Button>
            <Button variant="secondary" onClick={() => descarcaDecont(decont.id, 'xlsx')}>
              Decont Excel
            </Button>
            {decont.status === 'pending_review' && (
              <Button
                onClick={() => patchDecont.mutate({ status: 'verified' })}
                loading={patchDecont.isPending}
              >
                Marchează verificat
              </Button>
            )}
            {decont.status === 'verified' && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => patchDecont.mutate({ status: 'pending_review' })}
                >
                  Înapoi la verificare
                </Button>
                <Button onClick={() => exportDecont.mutate()} loading={exportDecont.isPending}>
                  Exportă decontul
                </Button>
              </>
            )}
            {(decont.status === 'new' || decont.status === 'extracted') && (
              <Button
                variant="secondary"
                onClick={() => patchDecont.mutate({ status: 'pending_review' })}
              >
                Începe verificarea
              </Button>
            )}
          </div>
        </div>

        <div className="flex gap-1 border-b border-b4l-border">
          <button
            onClick={() => setTab('randuri')}
            className={`px-4 py-2 text-base font-medium ${
              tab === 'randuri'
                ? '-mb-px border-b-2 border-b4l-text text-b4l-text'
                : 'text-b4l-text-muted hover:text-b4l-text'
            }`}
          >
            Rânduri decont
          </button>
          <button
            onClick={() => setTab('note')}
            className={`px-4 py-2 text-base font-medium ${
              tab === 'note'
                ? '-mb-px border-b-2 border-b4l-text text-b4l-text'
                : 'text-b4l-text-muted hover:text-b4l-text'
            }`}
          >
            Note contabile
          </button>
        </div>
      </AntetFix>

      {bonuriPeAltaLuna.length > 0 && (
        <Card>
          {/*
            Formularea spune AMANDOUA lunile, in aceeasi propozitie.

            Prima varianta scria doar data bonului langa butonul „Muta pe iulie 2026", iar asta se
            citea circular: „bonurile sunt pe iulie, mesajul spune muta pe iulie, ceva nu e in
            regula" (Alexandru, 19.09.2026). Lipsea tocmai partea care conteaza — ca ele stau
            acum in decontul pe AUGUST.
          */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setNepotrivireDeschisa((v) => !v)}
              className="flex items-center gap-1.5 text-left text-sm font-medium text-amber-700"
            >
              <ChevronRight
                size={14}
                className={`transition-transform ${nepotrivireDeschisa ? 'rotate-90' : ''}`}
              />
              Decontul e pe {lunaInCuvinte(decont.period)}, dar{' '}
              {bonuriPeAltaLuna.length === 1
                ? 'un bon are data'
                : `${bonuriPeAltaLuna.length} bonuri au data`}
              {lunaTintaUnica ? ` din ${lunaInCuvinte(lunaTintaUnica)}` : ' din alte luni'}.
            </button>

            {lunaTintaUnica && (
              <Button
                variant="secondary"
                className="!ml-auto !px-2 !py-1 !text-xs"
                loading={mutaToate.isPending}
                onClick={() => {
                  const cate = bonuriPeAltaLuna.length
                  const unde = lunaInCuvinte(lunaTintaUnica)
                  if (!confirm(`Muți ${cate} ${cate === 1 ? 'bonul' : 'bonurile'} pe ${unde}?`))
                    return
                  mutaToate.mutate({
                    fileIds: bonuriPeAltaLuna.map((b) => b.fileId),
                    period: lunaTintaUnica,
                  })
                }}
              >
                {bonuriPeAltaLuna.length === 1 ? 'Mută pe' : 'Mută toate pe'}{' '}
                {lunaInCuvinte(lunaTintaUnica)}
              </Button>
            )}
          </div>

          {nepotrivireDeschisa && (
            <div className="mt-2 space-y-1">
              <p className="text-xs text-b4l-text-muted">
                Mutarea nu se face singură — bonul pleacă doar dacă o ceri.
              </p>
              {bonuriPeAltaLuna.map((b) => (
                <div
                  key={b.fileId}
                  className="flex flex-wrap items-center gap-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5"
                >
                  <span className="truncate text-xs" title={b.nume}>
                    {b.nume}
                  </span>
                  <span className="text-xs text-b4l-text-muted">
                    data {new Date(b.docDate).toLocaleDateString('ro-RO')}
                  </span>
                  <Button
                    variant="secondary"
                    className="!ml-auto !px-2 !py-0.5 !text-xs"
                    loading={mutaFisier.isPending}
                    onClick={() => mutaFisier.mutate({ fileId: b.fileId, period: b.luna })}
                  >
                    Mută pe {lunaInCuvinte(b.luna)}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === 'note' && (
        <div className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
          <NoteContabileTab decontId={decontId} />
        </div>
      )}

      {tab === 'randuri' && (
        <div className="grid grid-cols-1 gap-4 lg:min-h-0 lg:flex-1 lg:grid-cols-2">
          {/* STÂNGA — linii + footer */}
          <div className="space-y-3 lg:min-h-0 lg:overflow-y-auto lg:pr-1">
            <Card>
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-base font-semibold">Rânduri decont</h2>
                {canEdit && (
                  <div className="flex gap-1">
                    <Button onClick={() => setAddKind('bon')} className="!px-2 !py-1 !text-sm">
                      + Bon
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setAddKind('diurna')}
                      className="!px-2 !py-1 !text-sm"
                    >
                      + Diurnă
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setAddKind('mileage')}
                      className="!px-2 !py-1 !text-sm"
                    >
                      + Km auto
                    </Button>
                  </div>
                )}
              </div>

              {canEdit && (bonuri.length > 0 || facturi.length > 0) && (
                <div className="mb-2 flex items-center gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => matchPartners.mutate()}
                    loading={matchPartners.isPending}
                    className="!px-2 !py-1 !text-sm"
                  >
                    Identifică parteneri în EA
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => dedupe.mutate()}
                    loading={dedupe.isPending}
                    className="!px-2 !py-1 !text-sm"
                  >
                    Elimină dublurile
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => rescan.mutate()}
                    loading={rescan.isPending}
                    className="!px-2 !py-1 !text-sm"
                  >
                    Re-scanează
                  </Button>
                  {(() => {
                    const missingCif = [...bonuri, ...facturi].filter(
                      (l) => !l.vendorCif || l.vendorCif.trim() === '',
                    ).length
                    if (missingCif === 0) return null
                    return (
                      <Button
                        variant="secondary"
                        onClick={() => reocrCif.mutate()}
                        loading={reocrCif.isPending}
                        className="!px-2 !py-1 !text-sm"
                      >
                        Re-citește CIF lipsă ({missingCif})
                      </Button>
                    )
                  })()}
                  {(() => {
                    const mism = [...bonuri, ...facturi].filter(
                      (l) => l.eaMatchStatus === 'name_mismatch',
                    ).length
                    const notFound = [...bonuri, ...facturi].filter(
                      (l) => l.eaMatchStatus === 'cif_not_found',
                    ).length
                    if (mism === 0 && notFound === 0) return null
                    return (
                      <span className="text-xs text-amber-700">
                        {notFound > 0 && `${notFound} parteneri negăsiți în EA `}
                        {mism > 0 && `· ${mism} nume diferă — verifică`}
                      </span>
                    )
                  })()}
                </div>
              )}

              <LineTable
                title="Bonuri"
                lines={bonuri}
                explicatii={explicatii}
                canEdit={canEdit}
                onEdit={setEditLine}
                activId={editLine?.id ?? detaliiLine?.id ?? randApasatId}
                fisierActivId={viewFileId}
                onDetalii={eAdmin ? setDetaliiLine : undefined}
                onDelete={(l) => deleteLine.mutate(l.id)}
                onView={(l) => l.sourceFileId && viewFile(l.sourceFileId, l.id)}
                onReocrSonnet={(l) => {
                  if (!l.sourceFileId) return
                  const others = bonuri.filter(
                    (x) => x.sourceFileId === l.sourceFileId && x.id !== l.id,
                  ).length
                  const msg =
                    others === 0
                      ? 'Re-extragi acest bon cu Claude Sonnet (mai precis dar mai scump)?'
                      : `Asta re-extrage TOATE bonurile din fișierul respectiv (${others + 1} bonuri, inclusiv editările manuale anterioare). Model Sonnet — mai precis dar mai scump. Continui?`
                  if (confirm(msg)) reocrSonnet.mutate(l.sourceFileId)
                }}
                reocrSonnetPending={reocrSonnet.isPending}
              />
              {speciale.length > 0 && (
                <LineTable
                  title="Diurnă și km auto"
                  lines={speciale}
                  canEdit={canEdit}
                  onEdit={setEditLine}
                  activId={editLine?.id ?? detaliiLine?.id ?? randApasatId}
                  fisierActivId={viewFileId}
                  onDetalii={eAdmin ? setDetaliiLine : undefined}
                  onDelete={(l) => deleteLine.mutate(l.id)}
                />
              )}
              {facturi.length > 0 && (
                <LineTable
                  title="Facturi plătite de angajat (din avans — export separat pe 401)"
                  lines={facturi}
                  explicatii={explicatii}
                  canEdit={canEdit}
                  onCreeazaInEa={
                    canEdit
                      ? (l) =>
                          setFurnizorNou({
                            name: l.vendorName ?? '',
                            cif: l.vendorCif ?? '',
                            tara: 'Romania',
                          })
                      : undefined
                  }
                  onEdit={setEditLine}
                  activId={editLine?.id ?? detaliiLine?.id ?? randApasatId}
                  fisierActivId={viewFileId}
                  onDetalii={eAdmin ? setDetaliiLine : undefined}
                  onDelete={(l) => deleteLine.mutate(l.id)}
                  onView={(l) => l.sourceFileId && viewFile(l.sourceFileId, l.id)}
                  onReocrSonnet={(l) => {
                    if (!l.sourceFileId) return
                    if (
                      confirm(
                        'Re-extragi această factură cu Claude Sonnet (mai precis dar mai scump)?',
                      )
                    )
                      reocrSonnet.mutate(l.sourceFileId)
                  }}
                  reocrSonnetPending={reocrSonnet.isPending}
                />
              )}
              <AddClientModal
                rol="furnizor"
                open={furnizorNou !== null}
                precompletare={furnizorNou}
                onClose={() => setFurnizorNou(null)}
                onCreated={() => {
                  setFurnizorNou(null)
                  // EA are acum partenerul: identificarea se reface pe tot decontul.
                  matchPartners.mutate()
                }}
              />
              {lines.length === 0 && (
                <p className="py-4 text-center text-sm text-b4l-text-muted">
                  Niciun rând încă. Rândurile apar automat după citirea bonurilor; le poți adăuga și
                  manual, cu butoanele de mai sus.
                </p>
              )}
            </Card>

            <Card>
              <h2 className="mb-2 text-base font-semibold">Recapitulare</h2>
              <div className="space-y-2 text-base">
                <FooterRow label="Avans primit">
                  <input
                    type="number"
                    step="0.01"
                    defaultValue={decont.avansPrimit}
                    disabled={!canEdit}
                    onBlur={(e) => {
                      const v = Number(e.target.value)
                      if (v !== Number(decont.avansPrimit)) patchDecont.mutate({ avansPrimit: v })
                    }}
                    className="w-32 rounded border border-b4l-border px-2 py-1 text-right tabular-nums"
                  />
                </FooterRow>
                <FooterRow label="Total cheltuieli">
                  <span className="tabular-nums">
                    {fmt(decont.totalCheltuieli)} {decont.currency}
                  </span>
                </FooterRow>
                {Number(decont.totalFacturaDirecta ?? 0) > 0 && (
                  <FooterRow label="Total facturi plătite de angajat">
                    <span className="tabular-nums text-b4l-text-muted">
                      {fmt(decont.totalFacturaDirecta)} {decont.currency}
                    </span>
                  </FooterRow>
                )}
                <FooterRow label={balanceLabel}>
                  <span className="font-semibold tabular-nums">
                    {fmt(decont.balance)} {decont.currency}
                  </span>
                </FooterRow>
                {decont.currency === 'EUR' && (
                  <FooterRow label="Curs valutar">
                    <input
                      type="number"
                      step="0.0001"
                      defaultValue={decont.currencyRate ?? ''}
                      disabled={!canEdit}
                      onBlur={(e) => {
                        const v = Number(e.target.value)
                        if (v > 0 && v !== Number(decont.currencyRate))
                          patchDecont.mutate({ currencyRate: v })
                      }}
                      className="w-32 rounded border border-b4l-border px-2 py-1 text-right tabular-nums"
                    />
                  </FooterRow>
                )}
              </div>
              {decont.currency === 'EUR' && !decont.employee.eaAccountCodeEur && (
                <p className="mt-2 rounded bg-red-50 p-2 text-xs text-red-700">
                  ⚠ Decontul e în EUR, dar angajatul nu are cont 542 în EUR. Cere contabilității
                  să-l deschidă în ExpertAccounts și să-l completeze la angajat înainte de export.
                </p>
              )}
            </Card>

            <CrossCheckCard decontId={decontId} />
          </div>

          {/* DREAPTA — viewer fișiere */}
          <div className="space-y-3 lg:min-h-0 lg:overflow-y-auto lg:pr-1">
            <Card>
              <div className="mb-2 flex items-baseline justify-between">
                <h2 className="text-base font-semibold">Fișiere sursă</h2>
                {query.data && query.data.ocrCostTotal > 0 && (
                  <span className="text-xs text-b4l-text-muted">
                    Cost citire automată:{' '}
                    <span className="tabular-nums">${query.data.ocrCostTotal.toFixed(4)}</span>
                  </span>
                )}
              </div>
              {(() => {
                const failed = decont.files.filter(
                  (f) => f.kind === 'bonuri_scan' && f.ocrStatus === 'failed',
                )
                if (failed.length === 0) return null
                return (
                  <div className="mb-2 flex items-center gap-2 rounded bg-red-50 px-2 py-1.5 text-xs">
                    <span className="text-red-700">
                      {failed.length} fișier(e) cu citirea eșuată
                    </span>
                    <Button
                      variant="secondary"
                      onClick={() => reocrAll.mutate(failed.map((f) => f.id))}
                      loading={reocrAll.isPending}
                      className="!px-2 !py-0.5 !text-sm"
                    >
                      Reia citirea
                    </Button>
                  </div>
                )
              })()}
              {(() => {
                const cuPropunere = decont.files.filter((f) => f.propunereLaData)
                if (cuPropunere.length === 0) return null
                return (
                  /*
                    Prima varianta a acestui banner avea acelasi aspect cu lista de fisiere de
                    dedesubt: aceleasi nume, aceleasi pastile albe, fond albastru abia vizibil.
                    Ieseau doua randuri de nume de fisiere, unul sub altul, imposibil de deosebit
                    — „NU gasesc bannerul recitiri", desi era pe ecran. Acum are rama, titlu si
                    butoane care arata a actiune, nu a filtru.
                  */
                  <div className="mb-3 rounded border-2 border-b4l-blue bg-b4l-blue-light p-2.5">
                    <div className="mb-2 text-sm font-semibold text-b4l-blue-dark">
                      ↻{' '}
                      {cuPropunere.length === 1
                        ? 'O recitire așteaptă confirmarea'
                        : `${cuPropunere.length} recitiri așteaptă confirmarea`}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {cuPropunere.map((f) => (
                        <button
                          key={f.id}
                          onClick={() => deschidePropunerea.mutate(f.id)}
                          disabled={deschidePropunerea.isPending}
                          className="rounded border border-b4l-blue bg-white px-2 py-1 text-xs font-medium text-b4l-blue-dark hover:bg-b4l-blue hover:text-white disabled:opacity-50"
                        >
                          {f.fileName}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-[11px] text-b4l-blue-dark">
                      Apasă pe un fișier ca să compari citirea de acum cu cea nouă. Nimic nu se
                      schimbă până nu confirmi.
                    </p>
                  </div>
                )
              })()}
              <div className="mb-2 flex flex-wrap gap-1">
                {decont.files.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => viewFile(f.id)}
                    className={`rounded border px-2.5 py-1 text-sm ${
                      viewFileId === f.id
                        ? 'border-b4l-blue bg-b4l-blue-light font-medium'
                        : 'border-b4l-border hover:bg-b4l-bg'
                    }`}
                  >
                    {f.fileName}
                    {f.ocrStatus === 'failed' && <span title={f.ocrError ?? ''}> ⚠</span>}
                    {f.propunereLaData && <span title="Are o recitire neconfirmată"> ↻</span>}
                  </button>
                ))}
                {decont.files.length === 0 && (
                  <span className="text-xs text-b4l-text-muted">Niciun fișier.</span>
                )}
              </div>
              {/*
                De ce e bonul pe luna asta, si cum il muti daca nu e a lui.
                Nota exista in baza de la reincadrarea automata, dar pana acum se vedea doar in
                ecranul de incarcare al angajatului — omul care corecta decontul nu avea de unde
                sa stie ca bonul a venit din alta luna.
              */}
              {(() => {
                const sel = decont.files.find((f) => f.id === viewFileId)
                if (!sel) return null
                return (
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    {sel.notaLuna && (
                      <p className="flex-1 basis-full text-xs text-b4l-text-muted">
                        {sel.notaLuna}
                      </p>
                    )}
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setMutaFileId(sel.id)
                        setLunaTinta('')
                      }}
                      className="!px-2 !py-0.5 !text-sm"
                    >
                      Mută bonul pe altă lună
                    </Button>
                    <Button
                      variant="secondary"
                      className="!px-2 !py-1 !text-xs"
                      loading={cereRefacere.isPending}
                      onClick={() => {
                        const m = prompt(
                          'Ce nu e în regulă cu poza? (ex. e tăiată, nu se citește suma)',
                        )
                        if (m?.trim()) cereRefacere.mutate({ fileId: sel.id, motiv: m.trim() })
                      }}
                      title="Angajatul vede cererea pe ecranul lui și refotografiază"
                    >
                      Cere o poză mai bună
                    </Button>
                    <Button
                      variant="secondary"
                      className="!px-2 !py-1 !text-xs !text-b4l-red"
                      loading={stergeBon.isPending}
                      onClick={() => {
                        if (confirm(`Ștergi „${sel.fileName}"? Poza merge în „_sterse".`)) {
                          stergeBon.mutate(sel.id)
                        }
                      }}
                    >
                      Șterge bonul
                    </Button>
                    <span className="text-xs text-b4l-text-muted">
                      Acum e pe {lunaInCuvinte(decont.period)} — de acolo vine și data (docd) care
                      pleacă în ExpertAccounts.
                    </span>
                  </div>
                )
              })()}
              {/* Preview inline doar pe desktop; pe mobil fișierul se deschide
                  full-screen (overlay), ca să nu apară mic la baza paginii. */}
              <div className="hidden lg:block">
                {viewFileId ? (
                  <FileViewer
                    fileId={viewFileId}
                    files={decont.files}
                    onReocr={invalidate}
                    onZoom={() => setOverlayFileId(viewFileId)}
                    pagina={paginaRandActiv}
                    semnal={randActivId}
                  />
                ) : (
                  <p className="py-8 text-center text-sm text-b4l-text-muted">
                    Selectează un fișier pentru previzualizare.
                  </p>
                )}
              </div>
              <p className="py-2 text-center text-sm text-b4l-text-muted lg:hidden">
                Apasă pe un fișier ca să-l deschizi pe tot ecranul.
              </p>
            </Card>
          </div>
        </div>
      )}

      {overlayFileId != null && (
        <FileOverlay
          fileId={overlayFileId}
          files={decont.files}
          onClose={() => setOverlayFileId(null)}
          pagina={
            randActiv && randActiv.sourceFileId === overlayFileId
              ? (randActiv.sourcePageNum ?? null)
              : null
          }
        />
      )}

      {mutaFileId != null && (
        <Modal
          open
          onClose={() => setMutaFileId(null)}
          title={`Mută pe altă lună: ${decont.files.find((f) => f.id === mutaFileId)?.fileName ?? ''}`}
        >
          <div className="space-y-3 text-sm">
            <p className="text-b4l-text-muted">
              Bonul (și liniile citite din el) trec pe decontul lunii alese, al aceluiași angajat.
              Dacă decontul acelei luni nu există, se creează. Fișierul rămâne unde e în Nextcloud —
              se schimbă doar luna de care aparține.
            </p>
            <p className="rounded border border-b4l-blue bg-b4l-blue-light p-2">
              Contează pentru export: în ExpertAccounts <strong>docd</strong> e ultima zi a lunii
              decontului, nu data de pe bon. Un bon pe luna greșită pleacă cu dată greșită oricât ai
              corecta „Data document” pe linie.
            </p>
            <div>
              <label className="mb-1 block text-sm font-medium">Luna pe care se mută</label>
              <select
                value={lunaTinta}
                onChange={(e) => setLunaTinta(e.target.value)}
                className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
              >
                <option value="">— alege luna —</option>
                {luniRecente().map((l) => (
                  <option key={l} value={l} disabled={l === decont.period}>
                    {lunaInCuvinte(l)}
                    {l === decont.period ? ' (e deja aici)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setMutaFileId(null)}>
                Anulează
              </Button>
              <Button
                disabled={!lunaTinta}
                loading={mutaFisier.isPending}
                onClick={() => mutaFisier.mutate({ fileId: mutaFileId, period: lunaTinta })}
              >
                Mută
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {comparatie && (
        <Modal
          open
          /*
            Inchiderea doar inchide. Pana acum arunca recitirea — un Escape apasat din greseala
            stergea o citire pentru care se platise. „Renunta" e la un centimetru mai jos, si
            spune ce face.
          */
          onClose={() => setComparatie(null)}
          title={`Recitire: ${comparatie.fileName}`}
          size="xl"
        >
          <div className="space-y-3 text-sm">
            <p className="text-b4l-text-muted">
              Recitirea nu înlocuiește nimic până nu confirmi. Modelele de citire nu dau de fiecare
              dată același rezultat, mai ales pe fișierele cu mai multe bonuri — de aceea le vezi pe
              amândouă.
            </p>

            {/*
              „Acum" numara DOAR liniile care vin din fisierul asta, nu tot decontul. Cand
              fisierul n-are nicio linie, iese „0,00 lei" langa un bon care se vede in poza si
              are, de exemplu, 112 lei in alta parte a listei — si pare o contradictie. Nu e:
              acel 112 vine din alt fisier.
            */}
            {comparatie.curent.length === 0 && (
              <p className="rounded border border-b4l-blue bg-b4l-blue-light p-2">
                Din fișierul ăsta nu e acum nicio linie în decont — de-aia scrie 0,00 lei în stânga.
                Dacă vezi aceeași sumă în altă parte a listei, vine din alt fișier. Confirmarea o
                adaugă, nu o înlocuiește.
              </p>
            )}

            {comparatie.pierdeBonuri && (
              <p className="rounded border border-b4l-red/30 bg-b4l-red-light/30 p-2">
                <strong>Atenție:</strong> recitirea a găsit {comparatie.propus.length} bonuri, față
                de {comparatie.curent.length} câte sunt acum. Dacă o accepți, diferența dispare din
                decont.
              </p>
            )}

            {/*
              Bonul, langa cele doua citiri. „Cand apas un buton cu recitire, vreau sa vad si
              bonul, altfel nu stiu ce confirm" — pana acum fereastra arata doua liste de cifre
              si atat, iar singura cale de a le verifica era sa inchizi si sa deschizi poza.
            */}
            <div className="grid gap-3 lg:grid-cols-2">
              <div>
                <p className="mb-1 font-medium">Bonul</p>
                <FileViewer
                  fileId={comparatie.fileId}
                  files={decont.files}
                  onReocr={invalidate}
                  onZoom={() => setOverlayFileId(comparatie.fileId)}
                  compact
                />
                <button
                  onClick={() => setOverlayFileId(comparatie.fileId)}
                  className="mt-1 text-xs text-b4l-blue-dark hover:underline"
                >
                  ⛶ Mărește pe tot ecranul
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="mb-1 font-medium">
                    Acum, din acest fișier — {comparatie.totalCurent.toFixed(2)} lei (
                    {comparatie.curent.length})
                  </p>
                  <ul className="space-y-0.5 text-xs">
                    {comparatie.curent.map((l, i) => (
                      <li key={i} className="rounded border border-b4l-border px-2 py-1">
                        {l.vendorName ?? '—'} · <strong>{l.amountTotal.toFixed(2)}</strong>
                        {l.docDate && ` · ${l.docDate}`}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="mb-1 font-medium">
                    Recitit cu un model mai puternic — {comparatie.totalPropus.toFixed(2)} lei (
                    {comparatie.propus.length})
                  </p>
                  <ul className="space-y-0.5 text-xs">
                    {comparatie.propus.map((l, i) => (
                      <li key={i} className="rounded border border-b4l-border px-2 py-1">
                        {l.vendorName ?? '—'} · <strong>{l.amountTotal.toFixed(2)}</strong>
                        {l.docDate && ` · ${l.docDate}`}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="secondary"
                loading={renuntaLaPropunere.isPending}
                onClick={() => renuntaLaPropunere.mutate(comparatie.fileId)}
              >
                Păstrează citirea actuală
              </Button>
              <Button
                loading={acceptaPropunerea.isPending}
                onClick={() => acceptaPropunerea.mutate(comparatie.fileId)}
              >
                Folosește citirea nouă
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {detaliiLine && (
        <DetaliiImportModal line={detaliiLine} onClose={() => setDetaliiLine(null)} />
      )}

      {(editLine || addKind) && (
        <LineFormModal
          decontId={decontId}
          line={editLine}
          createKind={addKind}
          categories={categories}
          onClose={() => {
            setEditLine(null)
            setAddKind(null)
          }}
          onSaved={() => {
            setEditLine(null)
            setAddKind(null)
            invalidate()
          }}
        />
      )}
    </div>
  )
}

interface GrupaImport {
  cota: number
  contCheltuiala: string
  suma: number
  tva: number
  total: number
}

interface DetaliiImport {
  doct: string
  docd: string
  dataBon: string | null
  docn: string
  partener: string
  partenerDinEa: boolean
  contCredit: string
  template: string
  grupe: GrupaImport[]
  total: { suma: number; tva: number; total: number }
  totalBon: number
  avertismente: string[]
}

/**
 * Ce intra in contabilitate de pe bonul asta.
 *
 * Cifrele NU se calculeaza aici: vin de la acelasi constructor care scrie notele contabile si
 * fisierul de import EA. Un calcul paralel in ecran ar arata altceva decat pleaca in EA — adica
 * exact greseala pe care fereastra asta trebuie s-o prinda.
 */
function DetaliiImportModal({ line, onClose }: { line: DecontLine; onClose: () => void }) {
  const q = useQuery<DetaliiImport>({
    queryKey: ['decont-linie-detalii', line.id],
    queryFn: async () =>
      (await api.get<DetaliiImport>(`/deconturi/lines/${line.id}/detalii-import`)).data,
  })
  const d = q.data

  return (
    <Modal open onClose={onClose} title={`Detalii import — ${line.vendorName ?? 'bon'}`} size="lg">
      {q.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}
      {q.isError && (
        <p className="text-sm text-red-600">Nu s-au putut citi detaliile pentru linia asta.</p>
      )}
      {d && (
        <div className="space-y-4">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
            <Camp eticheta="doct">{d.doct}</Camp>
            <Camp eticheta="docn">{d.docn || <Lipsa />}</Camp>
            <Camp eticheta="docd">{d.docd}</Camp>
            <Camp eticheta="Data de pe bon">{d.dataBon ?? <Lipsa />}</Camp>
            <div className="col-span-2 sm:col-span-4">
              <dt className="text-xs uppercase tracking-wide text-b4l-text-muted">Partener</dt>
              <dd className="text-sm">
                {d.partener || <Lipsa />}
                {d.partener && (
                  <span className="ml-2 text-xs text-b4l-text-muted">
                    {d.partenerDinEa ? 'denumire din EA' : 'denumire citită de pe bon'}
                  </span>
                )}
              </dd>
            </div>
          </dl>

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-1.5 pr-3 font-medium">Cont</th>
                <th className="py-1.5 pr-3 text-right font-medium">Sumă</th>
                <th className="py-1.5 pr-3 text-right font-medium">TVA</th>
                <th className="py-1.5 pr-3 text-right font-medium">Cotă</th>
                <th className="py-1.5 text-right font-medium">Total linie</th>
              </tr>
            </thead>
            <tbody>
              {d.grupe.map((g) => (
                <tr key={g.cota} className="border-b border-b4l-border">
                  <td className="py-1.5 pr-3 font-mono text-xs">
                    {g.contCheltuiala}
                    {g.tva > 0 && <span className="text-b4l-text-muted"> + 4426</span>}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{fmt(g.suma)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{fmt(g.tva)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{g.cota}%</td>
                  <td className="py-1.5 text-right font-medium tabular-nums">{fmt(g.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <td className="py-2 pr-3">Total bon</td>
                <td className="py-2 pr-3 text-right tabular-nums">{fmt(d.total.suma)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{fmt(d.total.tva)}</td>
                <td className="py-2 pr-3" />
                <td className="py-2 text-right tabular-nums">{fmt(d.total.total)}</td>
              </tr>
            </tfoot>
          </table>

          {d.avertismente.map((a) => (
            <p
              key={a}
              className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            >
              {a}
            </p>
          ))}

          <p className="text-xs text-b4l-text-muted">
            Contrapartida: <span className="font-mono">{d.contCredit || '—'}</span>
            {d.template && <> · șablon „{d.template}”</>}
          </p>
        </div>
      )}
    </Modal>
  )
}

function Camp({ eticheta, children }: { eticheta: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-b4l-text-muted">{eticheta}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  )
}

function Lipsa() {
  return <span className="text-amber-700">— lipsește</span>
}

function FooterRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-b4l-text-muted">{label}</span>
      {children}
    </div>
  )
}

// ============================================================================
// Cross-check cu decontul manual al Aurorei
// ============================================================================

interface AuroraRow {
  nr: number | null
  furnizor: string
  docRef: string
  suma: number
  explicatie: string
}

interface CrmLineRef {
  vendorName: string | null
  docRef: string | null
  docDate: string | null
  amount: number
  descriere: string
}

/** Nr. document + data, format compact pentru afisare. */
function docInfo(docRef: string | null | undefined, docDate?: string | null): string {
  const parts: string[] = []
  if (docRef) parts.push(docRef)
  if (docDate) parts.push(new Date(docDate).toLocaleDateString('ro-RO'))
  return parts.join(' · ')
}

interface CrossCheck {
  status: 'match' | 'mismatch' | 'no_reference' | 'error'
  message?: string
  fileName?: string
  aurora?: {
    nrDecont: number | null
    total: number
    avans: number
    rowCount: number
    rows: AuroraRow[]
  }
  crm: { total: number; avans: number; lineCount: number }
  diff?: { total: number; avans: number; lineCount: number }
  matchedCount?: number
  pairs?: CrossPair[]
}

interface CrossPair {
  crm: CrmLineRef | null
  aurora: AuroraRow | null
  matched: boolean
}

function CrossCheckCard({ decontId }: { decontId: number }) {
  const q = useQuery<{ crossCheck: CrossCheck }>({
    queryKey: ['cross-check', decontId],
    queryFn: async () =>
      (await api.get<{ crossCheck: CrossCheck }>(`/deconturi/${decontId}/cross-check`)).data,
  })

  const cc = q.data?.crossCheck

  return (
    <Card>
      <h2 className="mb-2 text-base font-semibold">Verificare cu decontul completat manual</h2>
      {q.isLoading ? (
        <p className="text-sm text-b4l-text-muted">Se verifică...</p>
      ) : !cc ? (
        <p className="text-sm text-b4l-text-muted">Indisponibil.</p>
      ) : cc.status === 'no_reference' ? (
        <p className="text-sm text-b4l-text-muted">
          Nu există decont manual de referință în folderul Nextcloud.
        </p>
      ) : cc.status === 'error' ? (
        <p className="text-sm text-red-600">{cc.message ?? 'Eroare la verificare'}</p>
      ) : (
        <>
          <div
            className={`mb-2 rounded px-2 py-1 text-xs font-medium ${
              cc.status === 'match' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
            }`}
          >
            {cc.status === 'match'
              ? '✓ Decontul CRM corespunde cu decontul manual'
              : '⚠ Discrepanță față de decontul manual'}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-1 pr-3 font-medium"></th>
                <th className="py-1 pr-3 text-right font-medium">CRM</th>
                <th className="py-1 pr-3 text-right font-medium">Manual</th>
                <th className="py-1 text-right font-medium">Δ</th>
              </tr>
            </thead>
            <tbody>
              <CrossRow
                label="Total cheltuieli"
                crm={fmt(cc.crm.total)}
                manual={fmt(cc.aurora?.total)}
                diff={cc.diff?.total ?? 0}
              />
              <CrossRow
                label="Avans primit"
                crm={fmt(cc.crm.avans)}
                manual={fmt(cc.aurora?.avans)}
                diff={cc.diff?.avans ?? 0}
              />
              <CrossRow
                label="Nr. rânduri"
                crm={String(cc.crm.lineCount)}
                manual={String(cc.aurora?.rowCount ?? 0)}
                diff={cc.diff?.lineCount ?? 0}
                integer
              />
            </tbody>
          </table>
          {cc.pairs && cc.pairs.length > 0 && (
            <div className="mt-3">
              <div className="mb-1 flex items-center justify-between">
                <h3 className="text-xs font-semibold">Rânduri — CRM vs decont manual</h3>
                <span className="text-xs text-b4l-text-muted">
                  {cc.matchedCount ?? 0} corespund · {cc.pairs.filter((p) => !p.matched).length}{' '}
                  diferențe
                </span>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                    <th className="py-1 pr-2 font-medium">Furnizor (CRM)</th>
                    <th className="py-1 pr-3 text-right font-medium">Sumă</th>
                    <th className="border-l border-b4l-border py-1 pl-3 pr-2 font-medium">
                      Furnizor (manual)
                    </th>
                    <th className="py-1 text-right font-medium">Sumă</th>
                  </tr>
                </thead>
                <tbody>
                  {cc.pairs.map((p, i) => (
                    <tr
                      key={i}
                      className={`border-b border-b4l-border last:border-0 ${
                        p.matched ? '' : 'bg-red-50 text-red-700'
                      }`}
                    >
                      <td
                        className="py-1 pr-2"
                        title={p.crm ? docInfo(p.crm.docRef, p.crm.docDate) : ''}
                      >
                        {p.crm?.vendorName ?? '—'}
                      </td>
                      <td className="py-1 pr-3 text-right tabular-nums">
                        {p.crm ? fmt(p.crm.amount) : '—'}
                      </td>
                      <td
                        className="border-l border-b4l-border py-1 pl-3 pr-2"
                        title={p.aurora ? docInfo(p.aurora.docRef) : ''}
                      >
                        {p.aurora?.furnizor ?? '—'}
                      </td>
                      <td className="py-1 text-right tabular-nums">
                        {p.aurora ? fmt(p.aurora.suma) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-b4l-border font-semibold">
                    <td className="py-1 pr-2">Total</td>
                    <td className="py-1 pr-3 text-right tabular-nums">
                      {fmt(cc.pairs.reduce((s, p) => s + (p.crm?.amount ?? 0), 0))}
                    </td>
                    <td className="border-l border-b4l-border py-1 pl-3 pr-2" />
                    <td className="py-1 text-right tabular-nums">
                      {fmt(cc.pairs.reduce((s, p) => s + (p.aurora?.suma ?? 0), 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
              <p className="mt-1 text-xs text-b4l-text-muted">
                Rândurile roșii apar doar într-o parte — verifică-le.
              </p>
            </div>
          )}

          {cc.fileName && (
            <p className="mt-2 text-xs text-b4l-text-muted">Referință: {cc.fileName}</p>
          )}
        </>
      )}
    </Card>
  )
}

function CrossRow({
  label,
  crm,
  manual,
  diff,
  integer = false,
}: {
  label: string
  crm: string
  manual: string
  diff: number
  integer?: boolean
}) {
  const ok = Math.abs(diff) < 0.001
  const diffStr = integer ? String(diff) : fmt(diff)
  return (
    <tr className="border-b border-b4l-border last:border-0">
      <td className="py-1 pr-3 text-b4l-text-muted">{label}</td>
      <td className="py-1 pr-3 text-right tabular-nums">{crm}</td>
      <td className="py-1 pr-3 text-right tabular-nums">{manual}</td>
      <td
        className={`py-1 text-right tabular-nums ${ok ? 'text-green-600' : 'text-amber-600 font-medium'}`}
      >
        {ok ? '✓' : diff > 0 ? `+${diffStr}` : diffStr}
      </td>
    </tr>
  )
}

/** Badge cu statusul identificarii partenerului in ExpertAccounts. */
function EaMatchBadge({ line }: { line: DecontLine }) {
  if (line.kind !== 'bon' && line.kind !== 'factura_directa') return null
  switch (line.eaMatchStatus) {
    case 'matched':
      return (
        <span
          title={`Partener EA: ${line.eaPartnerName ?? ''}`}
          className="rounded bg-green-100 px-1 text-xs text-green-700"
        >
          ✓ EA
        </span>
      )
    case 'name_mismatch':
      return (
        <span
          title={`Denumire în EA: ${line.eaPartnerName ?? ''} — verifică dacă CIF-ul e corect`}
          className="rounded bg-amber-100 px-1 text-xs text-amber-700"
        >
          ⚠ nume EA diferă
        </span>
      )
    case 'cif_not_found':
      return (
        <span
          title="Partenerul cu acest CIF nu există în ExpertAccounts. La o factură directă: butonul „Creează în EA”."
          className="rounded bg-red-100 px-1 text-xs text-red-700"
        >
          partener negăsit în EA
        </span>
      )
    case 'no_cif':
      return <span className="rounded bg-b4l-bg px-1 text-xs text-b4l-text-muted">fără CIF</span>
    default:
      return null
  }
}

/**
 * Randul e „activ" daca despre el e deschis ceva: previzualizarea bonului, fereastra de editare
 * sau detaliile de import.
 */
/**
 * Cat de tare se evidentiaza un rand.
 *
 * DOUA lucruri diferite, care aratau la fel pana acum si se citeau gresit:
 *
 *  - `apasat` — randul pe care chiar ai dat click;
 *  - `acelasi_fisier` — celelalte randuri care vin din acelasi document.
 *
 * Al doilea raspunde la „pentru care rand e poza din dreapta?", si raspunsul chiar sunt toate
 * cand un PDF are opt bonuri. Dar cu aceeasi culoare si acelasi chenar, opt randuri aprinse
 * deodata arata ca o selectie: „am selectat un bon si s-au marcat mai multe, cred ca toate"
 * (Alexandru, 19.09.2026). Acum indiciul e discret, iar evidentierea tare ramane a unuia singur.
 */
const CLASE_EVIDENTIERE: Record<string, string> = {
  apasat: 'bg-gray-200 ring-2 ring-inset ring-b4l-blue-dark',
  // Doar o urma de fundal: „vine din acelasi document", nu „e selectat".
  acelasi_fisier: 'bg-b4l-blue-light/40',
}

function nivelEvidentiere(
  l: DecontLine,
  activId: number | null | undefined,
  fisierActivId: number | null | undefined,
): 'apasat' | 'acelasi_fisier' | null {
  if (activId != null && l.id === activId) return 'apasat'
  if (l.sourceFileId != null && l.sourceFileId === fisierActivId) return 'acelasi_fisier'
  return null
}

function LineTable({
  title,
  lines,
  canEdit,
  onEdit,
  activId,
  fisierActivId,
  onDetalii,
  onDelete,
  onView,
  onReocrSonnet,
  reocrSonnetPending,
  onCreeazaInEa,
  explicatii,
}: {
  title: string
  lines: DecontLine[]
  /** Explicatia angajatului, pe fisierul din care vine randul (`sourceFileId`). */
  explicatii?: Map<number, string>
  canEdit: boolean
  onEdit: (l: DecontLine) => void
  /** Randul deschis acum — in editare SAU in detaliile de import. */
  activId?: number | null
  /** Fisierul deschis in previzualizare. Toate randurile care vin din el se coloreaza. */
  fisierActivId?: number | null
  /** Doar pentru admini — nedefinit in rest, si atunci butonul nici nu se randeaza. */
  onDetalii?: (l: DecontLine) => void
  onDelete: (l: DecontLine) => void
  onView?: (l: DecontLine) => void
  onReocrSonnet?: (l: DecontLine) => void
  reocrSonnetPending?: boolean
  /** Factură directă cu furnizor negăsit în EA: „Creează în EA". Nedefinit → fără buton. */
  onCreeazaInEa?: (l: DecontLine) => void
}) {
  if (lines.length === 0) return null
  const btn =
    'rounded border px-2.5 py-1 text-sm hover:bg-b4l-bg whitespace-nowrap border-b4l-border'
  return (
    <div className="mb-3">
      <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-b4l-text-muted">
        {title}
      </div>
      <div className="divide-y divide-b4l-border">
        {lines.map((l) => (
          <div
            key={l.id}
            /*
              Randul ACTIV — fond gri plus chenar.

              Activ inseamna oricare dintre cele trei: bonul lui e deschis in previzualizare
              (Vezi), randul e deschis in fereastra de editare (Editeaza), sau i se vad detaliile
              de import (Detalii bon). Toate trei deschid ceva care acopera jumatate de ecran si
              nu spune despre ce rand e vorba; pe un decont cu cinci „MEGA IMAGE SRL" nu aveai
              cum sa stii la care te uiti.

              La Vezi se coloreaza TOATE randurile din fisierul deschis, nu doar cel apasat: un
              PDF poate avea mai multe bonuri, si atunci raspunsul la „pentru care document din
              stanga e poza asta?" sunt toate.

              Vine PESTE bara de incredere din stanga, care spune cu totul altceva.
            */
            className={[
              'flex flex-col gap-2 py-2.5 pl-2 sm:flex-row sm:items-center sm:gap-3',
              confidenceClass(l),
              CLASE_EVIDENTIERE[nivelEvidentiere(l, activId, fisierActivId) ?? ''] ?? '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {/* info — furnizor + meta */}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-base font-medium">{l.vendorName ?? KIND_LABEL[l.kind]}</span>
                {(() => {
                  const p = confidencePill(l)
                  return p ? (
                    <span
                      title={p.title}
                      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs ${p.cls}`}
                    >
                      <span className={`inline-block h-2 w-2 rounded-full ${p.dot}`} />
                      {p.text}
                    </span>
                  ) : null
                })()}
                <EaMatchBadge line={l} />
                {onCreeazaInEa &&
                  l.kind === 'factura_directa' &&
                  l.eaMatchStatus === 'cif_not_found' && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        onCreeazaInEa(l)
                      }}
                      className="rounded border border-b4l-blue px-1.5 text-xs text-b4l-blue hover:bg-b4l-blue/10"
                      title="Adaugă furnizorul în nomenclator și în ExpertAccounts, apoi reidentifică"
                    >
                      Creează în EA
                    </button>
                  )}
              </div>
              <div className="text-sm text-b4l-text-muted">
                {[
                  l.vendorCif ? `CUI ${l.vendorCif}` : null,
                  l.descriereDetaliata || null,
                  l.category?.label ?? null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
              {l.sourceFileId != null && explicatii?.get(l.sourceFileId) && (
                <div className="text-sm">
                  <span className="text-b4l-text-muted">Angajatul: </span>„
                  {explicatii.get(l.sourceFileId)}”
                </div>
              )}
            </div>
            {/* sumă + acțiuni — pe telefon pe rând separat, pe desktop la dreapta */}
            <div className="flex items-center justify-between gap-2 sm:justify-end">
              <span className="text-base font-semibold tabular-nums sm:order-2 sm:w-28 sm:text-right">
                {fmt(l.amountTotal)}
              </span>
              <div className="flex flex-wrap gap-1 sm:order-1 sm:flex-nowrap">
                {onView && l.sourceFileId && (
                  <button onClick={() => onView(l)} className={btn}>
                    Vezi
                  </button>
                )}
                {canEdit && onReocrSonnet && l.sourceFileId && (
                  <button
                    onClick={() => onReocrSonnet(l)}
                    disabled={reocrSonnetPending}
                    className="whitespace-nowrap rounded border border-purple-200 bg-purple-50 px-2.5 py-1 text-sm text-purple-700 hover:bg-purple-100 disabled:opacity-50"
                    title="Re-citește cu un model mai puternic decât cel obișnuit. Mai precis, dar mai scump."
                  >
                    Recitește
                  </button>
                )}
                <button onClick={() => onEdit(l)} className={btn}>
                  {canEdit ? 'Editează' : 'Detalii'}
                </button>
                {onDetalii && (
                  <button
                    onClick={() => onDetalii(l)}
                    className={btn}
                    title="Ce intră în contabilitate de pe bonul ăsta"
                  >
                    Detalii bon
                  </button>
                )}
                {canEdit && (
                  <button
                    onClick={() => onDelete(l)}
                    className="whitespace-nowrap rounded border border-red-200 px-2.5 py-1 text-sm text-red-600 hover:bg-red-50"
                  >
                    Șterge
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Imaginea de aratat pentru un fisier: poza ca atare, sau pagina de PDF randata.
 *
 * Pe Android un PDF intr-un `<iframe>` nu se randeaza — browserul arata o pictograma cu
 * „Deschide". Randand pagina ca imagine merge peste tot, si pe deasupra zoom-ul si rotirea,
 * care erau oprite pentru PDF-uri, incep sa functioneze si acolo.
 */
function useImagineaDocumentului(
  fileId: number,
  url: string | undefined,
  estePdf: boolean,
  /** Pagina randului selectat; o noua selectie (`semnal`) sare acolo, chiar pe aceeasi pagina. */
  paginaDorita: number | null = null,
  semnal: number | null = null,
) {
  const [pagina, setPagina] = useState(paginaDorita ?? 1)
  useEffect(() => setPagina(paginaDorita ?? 1), [fileId, paginaDorita, semnal])

  const q = useQuery({
    queryKey: ['pdf-pagina', fileId, pagina],
    queryFn: () => randeazaPaginaPdf(url!, pagina),
    enabled: estePdf && !!url,
    staleTime: Infinity,
  })

  return {
    imagine: estePdf ? q.data?.imagine : url,
    nrPagini: q.data?.nrPagini ?? 1,
    pagina,
    setPagina,
    seIncarca: estePdf && q.isLoading,
    eroare: estePdf && q.isError,
  }
}

/** Sagetile de pagina, aratate doar la PDF-urile cu mai multe pagini. */
function Paginator({
  pagina,
  nrPagini,
  setPagina,
  clar = false,
}: {
  pagina: number
  nrPagini: number
  setPagina: (n: number) => void
  clar?: boolean
}) {
  if (nrPagini <= 1) return null
  const btn = clar
    ? 'rounded bg-white/20 px-2 py-0.5 text-white hover:bg-white/30 disabled:opacity-40'
    : 'rounded border border-b4l-border px-2 py-0.5 hover:bg-b4l-bg disabled:opacity-40'
  return (
    <div
      className={`flex items-center gap-2 text-xs ${clar ? 'text-white' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button className={btn} disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>
        ‹
      </button>
      <span className="tabular-nums">
        {pagina} / {nrPagini}
      </span>
      <button className={btn} disabled={pagina >= nrPagini} onClick={() => setPagina(pagina + 1)}>
        ›
      </button>
    </div>
  )
}

function FileViewer({
  fileId,
  files,
  onReocr,
  onZoom,
  compact = false,
  pagina = null,
  semnal = null,
}: {
  fileId: number
  files: DecontFile[]
  onReocr: () => void
  onZoom: () => void
  /** Pagina de aratat (a randului selectat); null = prima. */
  pagina?: number | null
  /** Id-ul randului selectat: o selectie noua readuce pagina lui. */
  semnal?: number | null
  /**
   * Doar poza, fara bara de comenzi.
   *
   * In fereastra de comparatie butonul „Re-ruleaza OCR" ar fi fost nu doar zgomot, ci
   * periculos: o recitire pornita chiar in timp ce te uiti la propunerea curenta ar
   * suprascrie-o, si ai fi confirmat altceva decat vedeai.
   */
  compact?: boolean
}) {
  const file = files.find((f) => f.id === fileId)
  const blobQuery = useQuery<string>({
    queryKey: ['decont-file-raw', fileId],
    queryFn: () => incarcaFisierBon(fileId),
    // O cerere picata o data (sesiune reinnoita, Nextcloud lent o clipa) nu lasa pagina pe eroare.
    retry: 2,
  })
  const reocr = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ file: { ocrStatus: string; ocrError: string | null } }>(
          `/deconturi/files/${fileId}/reocr`,
        )
      ).data,
    onSuccess: (data) => {
      onReocr()
      showReocrToast(data.file, 'Citirea a fost reluată')
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const isPdf =
    file?.mimeType === 'application/pdf' || !!file?.fileName.toLowerCase().endsWith('.pdf')
  const doc = useImagineaDocumentului(fileId, blobQuery.data, isPdf, pagina, semnal)

  if (!file) return null

  return (
    <div className="space-y-2">
      <div className={`flex flex-wrap items-center gap-2 text-sm ${compact ? 'hidden' : ''}`}>
        {file.kind === 'bonuri_scan' && (
          <>
            <span className="text-b4l-text-muted">Citire automată (OCR): {file.ocrStatus}</span>
            {file.ocrCost && (
              <span className="text-b4l-text-muted">
                · cost <span className="tabular-nums">${file.ocrCost.costUsd.toFixed(4)}</span>
                {file.ocrCost.calls > 1 ? ` (${file.ocrCost.calls} rulări)` : ''}
              </span>
            )}
            <Button
              variant="secondary"
              onClick={() => reocr.mutate()}
              loading={reocr.isPending}
              className="!px-2 !py-0.5 !text-sm"
            >
              Reia citirea
            </Button>
          </>
        )}
        {blobQuery.data && (
          <Button variant="secondary" onClick={onZoom} className="!px-2 !py-0.5 !text-sm">
            ⛶ Mărește
          </Button>
        )}
      </div>
      {blobQuery.isLoading || doc.seIncarca ? (
        <p className="py-8 text-center text-sm text-b4l-text-muted">Se încarcă fișierul...</p>
      ) : blobQuery.isError ? (
        <div className="py-8 text-center text-sm text-red-600">
          <p>Nu s-a putut încărca fișierul: {(blobQuery.error as Error)?.message}</p>
          <button
            type="button"
            onClick={() => blobQuery.refetch()}
            className="mt-2 rounded border border-b4l-border px-3 py-1 text-b4l-text hover:bg-b4l-bg-soft"
          >
            Reîncearcă
          </button>
        </div>
      ) : doc.eroare ? (
        <p className="py-8 text-center text-sm text-red-600">Nu s-a putut afișa pagina din PDF.</p>
      ) : (
        <>
          <img
            src={doc.imagine}
            alt={file.fileName}
            onClick={onZoom}
            className={`w-full cursor-zoom-in rounded border border-b4l-border object-contain ${
              compact ? 'max-h-[46vh]' : 'max-h-[70vh]'
            }`}
          />
          <div className="mt-1 flex justify-center">
            <Paginator pagina={doc.pagina} nrPagini={doc.nrPagini} setPagina={doc.setPagina} />
          </div>
        </>
      )}
    </div>
  )
}

/** Overlay full-screen cu fisierul (imagine/PDF), descarcat din Nextcloud. */
function FileOverlay({
  fileId,
  files,
  onClose,
  pagina = null,
}: {
  fileId: number
  files: DecontFile[]
  onClose: () => void
  pagina?: number | null
}) {
  const file = files.find((f) => f.id === fileId)
  const [extraRot, setExtraRot] = useState(0)
  const [scale, setScale] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const dragRef = useRef<{ x: number; y: number } | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const blobQuery = useQuery<string>({
    queryKey: ['decont-file-raw', fileId],
    queryFn: () => incarcaFisierBon(fileId),
    // O cerere picata o data (sesiune reinnoita, Nextcloud lent o clipa) nu lasa pagina pe eroare.
    retry: 2,
  })

  // Zoom cu scroll-ul mouse-ului. Listener nativ non-passive ca sa putem opri
  // scroll-ul paginii din spate.
  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      setScale((s) => Math.min(8, Math.max(1, e.deltaY < 0 ? s * 1.15 : s / 1.15)))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [blobQuery.data])

  // La revenirea pe 100% reseteaza si deplasarea.
  useEffect(() => {
    if (scale === 1) setPan({ x: 0, y: 0 })
  }, [scale])

  const isPdf =
    file?.mimeType === 'application/pdf' || !!file?.fileName.toLowerCase().endsWith('.pdf')
  const doc = useImagineaDocumentului(fileId, blobQuery.data, isPdf, pagina)

  if (!file) return null
  const rotation = extraRot % 360
  const resetZoom = () => {
    setScale(1)
    setPan({ x: 0, y: 0 })
  }
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/90" onClick={onClose}>
      <div className="flex items-center justify-between gap-2 px-4 py-2 text-sm text-white">
        <span className="min-w-0 truncate">{file.fileName}</span>
        <div className="flex shrink-0 items-center gap-2">
          {doc.imagine && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setScale((s) => Math.max(1, s / 1.4))
                }}
                className="rounded bg-white/20 px-3 py-1 hover:bg-white/30"
                title="Micșorează"
              >
                −
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setScale((s) => Math.min(8, s * 1.4))
                }}
                className="rounded bg-white/20 px-3 py-1 hover:bg-white/30"
                title="Mărește"
              >
                ＋
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setExtraRot((r) => (r + 90) % 360)
                }}
                className="rounded bg-white/20 px-3 py-1 hover:bg-white/30"
                title="Rotește 90°"
              >
                ↻ Rotește
              </button>
            </>
          )}
          <Paginator pagina={doc.pagina} nrPagini={doc.nrPagini} setPagina={doc.setPagina} clar />
          <button onClick={onClose} className="rounded bg-white/20 px-3 py-1 hover:bg-white/30">
            ✕ Închide
          </button>
        </div>
      </div>
      {blobQuery.isLoading || doc.seIncarca ? (
        <p className="py-8 text-center text-sm text-white/70">Se încarcă...</p>
      ) : blobQuery.isError ? (
        <div className="py-8 text-center text-sm text-red-300">
          <p>Nu s-a putut încărca fișierul: {(blobQuery.error as Error)?.message}</p>
          <button
            type="button"
            onClick={() => blobQuery.refetch()}
            className="mt-2 rounded border border-white/30 px-3 py-1 text-white hover:bg-white/10"
          >
            Reîncearcă
          </button>
        </div>
      ) : doc.eroare ? (
        <p className="py-8 text-center text-sm text-red-300">Nu s-a putut afișa pagina din PDF.</p>
      ) : (
        <div
          ref={wrapRef}
          className="flex min-h-0 flex-1 items-center justify-center overflow-hidden p-2"
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={resetZoom}
          onMouseMove={(e) => {
            if (!dragRef.current) return
            setPan({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y })
          }}
          onMouseUp={() => {
            dragRef.current = null
            setDragging(false)
          }}
          onMouseLeave={() => {
            dragRef.current = null
            setDragging(false)
          }}
        >
          <img
            src={doc.imagine}
            alt={file.fileName}
            draggable={false}
            onMouseDown={(e) => {
              if (scale <= 1) return
              dragRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }
              setDragging(true)
            }}
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale}) rotate(${rotation}deg)`,
              cursor: scale > 1 ? (dragging ? 'grabbing' : 'grab') : 'default',
            }}
            className="max-h-full max-w-full select-none object-contain"
          />
        </div>
      )}
      {doc.imagine && (
        <div className="pb-2 text-center text-xs text-white/50">
          Rotiță = mărire · trage = mută · dublu clic = revenire
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Modal editare / adăugare linie
// ============================================================================

interface LineFormState {
  descriereDetaliata: string
  categoryId: string
  vendorName: string
  docRef: string
  docDate: string
  vendorCif: string
  cumparatorCif: string
  tvaDeductibil: boolean
  amountTotal: string
  amountNet: string
  amountVat: string
  vatRate: string
  amountNet2: string
  amountVat2: string
  vatRate2: string
  accountCode: string
  costCenterId: string
  nrZile: string
  rataNeimpozabila: string
  km: string
  consumPer100km: string
  pretLitru: string
  needsReview: boolean
}

function lineToState(line: DecontLine | null): LineFormState {
  // Daca breakdown-ul TVA e setat in DB, recalculam Total din breakdown
  // (sursa de adevar) — Total-ul vechi din DB poate fi inconsistent cu
  // breakdown-ul (ex: OCR a sumat gresit, user a editat doar TVA).
  const n1 = Number(line?.amountNet ?? '') || 0
  const v1 = Number(line?.amountVat ?? '') || 0
  const n2 = Number(line?.amountNet2 ?? '') || 0
  const v2 = Number(line?.amountVat2 ?? '') || 0
  const breakdownSum = n1 + v1 + n2 + v2
  const computedTotal = breakdownSum > 0 ? breakdownSum.toFixed(2) : (line?.amountTotal ?? '')
  return {
    descriereDetaliata: line?.descriereDetaliata ?? '',
    categoryId: line?.categoryId ? String(line.categoryId) : '',
    vendorName: line?.vendorName ?? '',
    docRef: line?.docRef ?? '',
    docDate: line?.docDate ? line.docDate.slice(0, 10) : '',
    vendorCif: line?.vendorCif ?? '',
    cumparatorCif: line?.cumparatorCif ?? '',
    tvaDeductibil: line?.tvaDeductibil ?? true,
    amountTotal: computedTotal,
    amountNet: line?.amountNet ?? '',
    amountVat: line?.amountVat ?? '',
    vatRate: line?.vatRate ?? '',
    amountNet2: line?.amountNet2 ?? '',
    amountVat2: line?.amountVat2 ?? '',
    vatRate2: line?.vatRate2 ?? '',
    accountCode: line?.accountCode ?? '',
    costCenterId: line?.costCenterId ? String(line.costCenterId) : '',
    nrZile: line?.nrZile ? String(line.nrZile) : '',
    rataNeimpozabila: line?.rataNeimpozabila ?? '57.50',
    km: line?.km ? String(line.km) : '',
    consumPer100km: line?.consumPer100km ?? '',
    pretLitru: line?.pretLitru ?? '',
    needsReview: line?.needsReview ?? false,
  }
}

function LineFormModal({
  decontId,
  line,
  createKind,
  categories,
  onClose,
  onSaved,
}: {
  decontId: number
  line: DecontLine | null
  createKind: LineKind | null
  categories: ExpenseCategory[]
  onClose: () => void
  onSaved: () => void
}) {
  const kind: LineKind = line?.kind ?? createKind ?? 'bon'
  const isCreate = !line
  const [s, setS] = useState<LineFormState>(() => lineToState(line))
  const [showAdvanced, setShowAdvanced] = useState(() => !!line?.accountCode)
  const set = <K extends keyof LineFormState>(k: K, v: LineFormState[K]) =>
    setS((prev) => ({ ...prev, [k]: v }))

  const ccQuery = useQuery<{ items: CostCenter[] }>({
    queryKey: ['cost-centers'],
    queryFn: async () => (await api.get<{ items: CostCenter[] }>('/settings/cost-centers')).data,
  })
  const costCenters = useMemo(() => ccQuery.data?.items ?? [], [ccQuery.data])

  // La schimbarea categoriei, preia automat centrul de cost implicit al categoriei
  // (utilizatorul îl poate schimba apoi).
  const onCategoryChange = (val: string) => {
    setS((prev) => {
      const cat = categories.find((c) => String(c.id) === val)
      return {
        ...prev,
        categoryId: val,
        costCenterId: cat?.defaultCostCenterId
          ? String(cat.defaultCostCenterId)
          : prev.costCenterId,
      }
    })
  }

  const num = (v: string): number | undefined => (v.trim() === '' ? undefined : Number(v))

  /**
   * Setter pentru campurile breakdown TVA — recalculeaza si Total in ACELASI
   * setS, ca update-ul sa fie atomic (fara delay perceput intre tasta si refresh
   * Total).
   */
  const setBreakdown = (
    field: 'amountNet' | 'amountVat' | 'amountNet2' | 'amountVat2',
    value: string,
  ) => {
    if (kind !== 'bon' && kind !== 'factura_directa') {
      set(field, value)
      return
    }
    setS((prev) => {
      const next = { ...prev, [field]: value }
      const n1 = Number(next.amountNet) || 0
      const v1 = Number(next.amountVat) || 0
      const n2 = Number(next.amountNet2) || 0
      const v2 = Number(next.amountVat2) || 0
      const sum = n1 + v1 + n2 + v2
      if (sum > 0) next.amountTotal = sum.toFixed(2)
      return next
    })
  }

  const previewAmount = useMemo(() => {
    if (kind === 'diurna') return (num(s.nrZile) ?? 0) * (num(s.rataNeimpozabila) ?? 0)
    if (kind === 'mileage')
      return ((num(s.km) ?? 0) / 100) * (num(s.consumPer100km) ?? 0) * (num(s.pretLitru) ?? 0)
    return num(s.amountTotal) ?? 0
  }, [kind, s])

  const save = useMutation({
    mutationFn: async () => {
      const common = {
        descriereDetaliata: s.descriereDetaliata,
        categoryId: num(s.categoryId) ?? null,
        accountCode: s.accountCode.trim() || null,
        costCenterId: num(s.costCenterId) ?? null,
      }
      let body: Record<string, unknown>
      if (kind === 'bon' || kind === 'factura_directa') {
        body = {
          ...common,
          vendorName: s.vendorName.trim() || null,
          docRef: s.docRef.trim() || null,
          docDate: s.docDate || null,
          vendorCif: s.vendorCif.trim() || null,
          cumparatorCif: s.cumparatorCif.trim() || null,
          tvaDeductibil: s.tvaDeductibil,
          amountTotal: num(s.amountTotal) ?? 0,
          amountNet: num(s.amountNet) ?? null,
          amountVat: num(s.amountVat) ?? null,
          vatRate: num(s.vatRate) ?? null,
          amountNet2: num(s.amountNet2) ?? null,
          amountVat2: num(s.amountVat2) ?? null,
          vatRate2: num(s.vatRate2) ?? null,
        }
      } else if (kind === 'diurna') {
        body = {
          ...common,
          nrZile: num(s.nrZile) ?? 1,
          rataNeimpozabila: num(s.rataNeimpozabila) ?? 0,
        }
      } else {
        body = {
          ...common,
          km: num(s.km) ?? 1,
          consumPer100km: num(s.consumPer100km) ?? 0,
          pretLitru: num(s.pretLitru) ?? 0,
        }
      }
      if (isCreate) {
        return api.post(`/deconturi/${decontId}/lines`, { kind, ...body })
      }
      return api.patch(`/deconturi/lines/${line!.id}`, { ...body, needsReview: s.needsReview })
    },
    onSuccess: () => {
      toast.success(isCreate ? 'Linie adăugată' : 'Linie salvată')
      onSaved()
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={`${isCreate ? 'Adaugă' : 'Editează'} — ${KIND_LABEL[kind]}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            {isCreate ? 'Adaugă' : 'Salvează'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field
          label="Descriere (categoria reală / context)"
          value={s.descriereDetaliata}
          onChange={(e) => set('descriereDetaliata', e.target.value)}
        />

        {(kind === 'bon' || kind === 'factura_directa') && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Field
                label="Furnizor"
                value={s.vendorName}
                onChange={(e) => set('vendorName', e.target.value)}
              />
              <Field
                label="CIF furnizor"
                value={s.vendorCif}
                onChange={(e) => set('vendorCif', e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field
                label="Nr. document"
                value={s.docRef}
                onChange={(e) => set('docRef', e.target.value)}
              />
              <Field
                label="Data document"
                type="date"
                value={s.docDate}
                onChange={(e) => set('docDate', e.target.value)}
              />
            </div>
            {(() => {
              const hasBreakdown =
                (num(s.amountNet) ?? 0) +
                  (num(s.amountVat) ?? 0) +
                  (num(s.amountNet2) ?? 0) +
                  (num(s.amountVat2) ?? 0) >
                0
              return (
                <Field
                  label={
                    hasBreakdown
                      ? 'Total (cu TVA) — calculat automat din bază + TVA'
                      : 'Total (cu TVA)'
                  }
                  type="number"
                  step="0.01"
                  value={s.amountTotal}
                  onChange={(e) => set('amountTotal', e.target.value)}
                  disabled={hasBreakdown}
                />
              )
            })()}
            <div className="rounded bg-b4l-bg p-2">
              <div className="mb-1 text-xs font-medium text-b4l-text-muted">TVA cota 1</div>
              <div className="grid grid-cols-3 gap-2">
                <Field
                  label="Bază"
                  type="number"
                  step="0.01"
                  value={s.amountNet}
                  onChange={(e) => setBreakdown('amountNet', e.target.value)}
                />
                <Field
                  label="TVA"
                  type="number"
                  step="0.01"
                  value={s.amountVat}
                  onChange={(e) => setBreakdown('amountVat', e.target.value)}
                />
                <Field
                  label="Cotă %"
                  type="number"
                  value={s.vatRate}
                  onChange={(e) => set('vatRate', e.target.value)}
                />
              </div>
              <div className="mb-1 mt-2 text-xs font-medium text-b4l-text-muted">
                TVA cota 2 (opțional — bon cu 21% + 11%)
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Field
                  label="Bază 2"
                  type="number"
                  step="0.01"
                  value={s.amountNet2}
                  onChange={(e) => setBreakdown('amountNet2', e.target.value)}
                />
                <Field
                  label="TVA 2"
                  type="number"
                  step="0.01"
                  value={s.amountVat2}
                  onChange={(e) => setBreakdown('amountVat2', e.target.value)}
                />
                <Field
                  label="Cotă 2 %"
                  type="number"
                  value={s.vatRate2}
                  onChange={(e) => set('vatRate2', e.target.value)}
                />
              </div>
              <div className="mt-3 border-t border-b4l-border pt-2">
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={s.tvaDeductibil}
                    onChange={(e) => set('tvaDeductibil', e.target.checked)}
                  />
                  <span>
                    <span className="font-medium">TVA deductibil</span> — bifează doar dacă pe bon
                    apare CUI cumpărător (37126475 — Divers Seo Market SRL). Altfel TVA-ul NU se
                    deduce (art. 319 Cod Fiscal) și toată suma intră pe cont cheltuială.
                  </span>
                </label>
                <Field
                  label="CUI cumpărător detectat pe bon (opțional)"
                  type="text"
                  value={s.cumparatorCif}
                  onChange={(e) => set('cumparatorCif', e.target.value)}
                  className="mt-2"
                />
              </div>
            </div>
          </>
        )}

        {kind === 'diurna' && (
          <div className="grid grid-cols-2 gap-2">
            <Field
              label="Număr zile"
              type="number"
              value={s.nrZile}
              onChange={(e) => set('nrZile', e.target.value)}
            />
            <Field
              label="Rată neimpozabilă / zi"
              type="number"
              step="0.01"
              value={s.rataNeimpozabila}
              onChange={(e) => set('rataNeimpozabila', e.target.value)}
            />
          </div>
        )}

        {kind === 'mileage' && (
          <div className="grid grid-cols-3 gap-2">
            <Field
              label="Km"
              type="number"
              value={s.km}
              onChange={(e) => set('km', e.target.value)}
            />
            <Field
              label="Consum L/100km"
              type="number"
              step="0.01"
              value={s.consumPer100km}
              onChange={(e) => set('consumPer100km', e.target.value)}
            />
            <Field
              label="Preț / litru"
              type="number"
              step="0.001"
              value={s.pretLitru}
              onChange={(e) => set('pretLitru', e.target.value)}
            />
          </div>
        )}

        {(kind === 'diurna' || kind === 'mileage') && (
          <div className="rounded bg-b4l-bg p-2 text-sm">
            Sumă calculată: <span className="font-semibold tabular-nums">{fmt(previewAmount)}</span>
          </div>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium">
            Categorie cheltuială (contul de cheltuială)
          </label>
          <select
            value={s.categoryId}
            onChange={(e) => onCategoryChange(e.target.value)}
            className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
          >
            <option value="">— fără —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label} ({c.accountCode})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">Centru de cost</label>
          <select
            value={s.costCenterId}
            onChange={(e) => set('costCenterId', e.target.value)}
            className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
          >
            <option value="">— fără centru —</option>
            {line?.costCenter && !costCenters.some((c) => c.id === line.costCenter!.id) && (
              <option value={line.costCenter.id}>
                {line.costCenter.code} — {line.costCenter.name}
              </option>
            )}
            {costCenters.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} — {c.name}
                {c.department ? ` (${c.department})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Cont contabil specific — caz rar; ascuns sub „Avansat" ca să nu se
            suprapună confuz cu contul din categorie. */}
        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="text-xs text-b4l-text-muted underline hover:text-b4l-text"
          >
            {showAdvanced ? 'Ascunde opțiunile avansate' : 'Avansat: schimbă contul contabil'}
          </button>
          {showAdvanced && (
            <div className="mt-1">
              <select
                value={s.accountCode}
                onChange={(e) => set('accountCode', e.target.value)}
                className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
              >
                <option value="">— folosește contul din categorie —</option>
                {PLAN_CONTURI.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-b4l-text-muted">
                Lasă gol ca să folosești contul categoriei. Schimbă doar dacă bonul trebuie pe un
                analitic anume.
              </p>
            </div>
          )}
        </div>

        {!isCreate && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!s.needsReview}
              onChange={(e) => set('needsReview', !e.target.checked)}
            />
            Verificat (scoate din lista „necesită verificare”)
          </label>
        )}
      </div>
    </Modal>
  )
}

/**
 * Raportul decontului — formularul PDF, generat din randurile de acum, aratat in pagina
 * (Alexandru, 02.11.2026: „adaugă și vizualizare raport decont”). Se cere din nou la fiecare
 * deschidere, deci arata corecturile facute intre timp.
 */
function RaportDecont({
  decontId,
  titlu,
  onClose,
}: {
  decontId: number
  titlu: string
  onClose: () => void
}) {
  const q = useQuery<string>({
    queryKey: ['decont-raport', decontId],
    queryFn: async () => {
      try {
        const res = await api.get(`/deconturi/${decontId}/fisier?format=pdf`, {
          responseType: 'blob',
          timeout: 120_000,
        })
        return URL.createObjectURL(new Blob([res.data as Blob], { type: 'application/pdf' }))
      } catch (err) {
        throw new Error(await mesajDinBlob(err, 'Raportul nu s-a putut genera'))
      }
    },
    gcTime: 0,
    staleTime: 0,
    retry: 1,
  })
  useEffect(() => {
    const url = q.data
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [q.data])
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-b4l-border px-4 py-2">
          <span className="font-medium">{titlu}</span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => descarcaDecont(decontId, 'pdf')}>
              Descarcă PDF
            </Button>
            <Button variant="secondary" onClick={() => descarcaDecont(decontId, 'xlsx')}>
              Descarcă Excel
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Închide
            </Button>
          </div>
        </div>
        {q.isLoading ? (
          <p className="py-16 text-center text-sm text-b4l-text-muted">Se generează raportul…</p>
        ) : q.isError ? (
          <div className="py-16 text-center text-sm text-red-600">
            <p>{(q.error as Error).message}</p>
            <button
              type="button"
              onClick={() => q.refetch()}
              className="mt-2 rounded border border-b4l-border px-3 py-1 text-b4l-text hover:bg-b4l-bg-soft"
            >
              Reîncearcă
            </button>
          </div>
        ) : (
          <iframe title={titlu} src={q.data} className="h-full w-full flex-1" />
        )}
      </div>
    </div>
  )
}

/** Fisierul bonului, ca URL local; la eroare, motivul trimis de server (vine ca Blob). */
async function incarcaFisierBon(fileId: number): Promise<string> {
  try {
    const res = await api.get(`/deconturi/files/${fileId}/raw`, { responseType: 'blob' })
    return URL.createObjectURL(res.data as Blob)
  } catch (err) {
    throw new Error(await mesajDinBlob(err, 'serverul nu a răspuns'))
  }
}

/**
 * Descarca formularul decontului (Excel sau PDF), generat din randurile de acum — cel pe care
 * angajatii il incarcau singuri pana in septembrie 2026.
 */
async function descarcaDecont(id: number, format: 'xlsx' | 'pdf') {
  try {
    const res = await api.get(`/deconturi/${id}/fisier?format=${format}`, {
      responseType: 'blob',
      timeout: 120_000,
    })
    const cd = String(res.headers['content-disposition'] ?? '')
    const nume = /filename="([^"]+)"/.exec(cd)?.[1] ?? `decont-${id}.${format}`
    const href = URL.createObjectURL(res.data as Blob)
    const a = document.createElement('a')
    a.href = href
    a.download = nume
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(href)
  } catch (err) {
    toast.error(await mesajDinBlob(err, 'Descărcarea n-a reușit'))
  }
}

/** Mesajul de eroare al serverului, cand raspunsul a fost cerut ca Blob (responseType). */
async function mesajDinBlob(err: unknown, implicit: string): Promise<string> {
  const data = (err as { response?: { data?: unknown } }).response?.data
  if (data instanceof Blob) {
    try {
      return (
        (JSON.parse(await data.text()) as { error?: { message?: string } }).error?.message ??
        implicit
      )
    } catch {
      /* raspuns fara JSON */
    }
  }
  return implicit
}

function extractError(err: unknown): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  )
}

// ============================================================================
// Tab Note contabile
// ============================================================================

function NotaRow({ nota, decontId }: { nota: NotaContabila; decontId: number }) {
  const queryClient = useQueryClient()
  const invalidateKeys = useMemo(() => [['note-contabile', decontId]], [decontId])
  const del = useMutation({
    mutationFn: async () => api.delete(`/deconturi/note-contabile/${nota.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['note-contabile', decontId] })
      toast.success('Notă ștearsă')
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  return (
    <tr className="border-b border-b4l-border last:border-0">
      <td className="py-1 pr-2 text-b4l-text-muted">{nota.ordinal}</td>
      <NotaRowCells nota={nota} invalidateKeys={invalidateKeys} />
      <td className="py-1 text-right">
        <button
          onClick={() => {
            if (confirm('Ștergi nota contabilă?')) del.mutate()
          }}
          className="px-1 text-sm text-red-600 hover:text-red-800"
          title="Șterge"
        >
          ×
        </button>
      </td>
    </tr>
  )
}

function NoteContabileTab({ decontId }: { decontId: number }) {
  const queryClient = useQueryClient()
  const q = useQuery<{ note: NotaContabila[]; stare?: { vechi: boolean; editate: boolean } }>({
    queryKey: ['note-contabile', decontId],
    queryFn: async () => (await api.get(`/deconturi/${decontId}/note-contabile`)).data,
  })
  const note = useMemo(() => q.data?.note ?? [], [q.data])
  const vechi = q.data?.stare?.vechi === true

  const genereaza = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ result: { created: number } }>(
          `/deconturi/${decontId}/note-contabile/genereaza`,
        )
      ).data,
    onSuccess: (d) => {
      queryClient.invalidateQueries({ queryKey: ['note-contabile', decontId] })
      toast.success(`${d.result.created} note contabile generate`)
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const sterge = useMutation({
    mutationFn: async () =>
      (await api.delete<{ deleted: number }>(`/deconturi/${decontId}/note-contabile`)).data,
    onSuccess: (d) => {
      queryClient.invalidateQueries({ queryKey: ['note-contabile', decontId] })
      toast.success(`${d.deleted} note șterse`)
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const total = note.reduce((s, n) => s + Number(n.suma), 0)

  return (
    <Card>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-base font-semibold">Note contabile</h2>
        <div className="flex gap-2">
          {note.length > 0 && (
            <Button
              variant="secondary"
              onClick={() => {
                if (confirm(`Ștergi toate cele ${note.length} note contabile?`)) sterge.mutate()
              }}
              loading={sterge.isPending}
              className="!border-red-200 !px-2 !py-1 !text-sm !text-red-600 hover:!bg-red-50"
            >
              Șterge toate
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              if (
                note.length === 0 ||
                confirm('Regenerează notele din bonuri? Editările manuale se pierd.')
              )
                genereaza.mutate()
            }}
            loading={genereaza.isPending}
            className="!px-2 !py-1 !text-sm"
          >
            {note.length === 0 ? 'Generează note contabile' : 'Regenerează din bonuri'}
          </Button>
        </div>
      </div>

      {/*
        Notele nu se refac singure cand se schimba bonurile. Fara avertismentul asta, contabilitatea
        vede note care arata bine si nu are de unde sti ca un bon a fost adaugat sau sters dupa.
      */}
      {vechi && (
        <div className="mb-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Bonurile s-au schimbat după ce s-au generat notele — notele nu mai corespund decontului.
          {q.data?.stare?.editate
            ? ' Notele au și corecturi făcute de mână: verifică-le, apoi apasă „Regenerează din bonuri” (corecturile se pierd). Până atunci exportul e oprit.'
            : ' La export se refac singure din bonuri; le poți reface și acum, cu „Regenerează din bonuri”.'}
        </div>
      )}
      {q.isLoading ? (
        <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
      ) : note.length === 0 ? (
        <p className="text-sm text-b4l-text-muted">
          Nicio notă contabilă încă. Apasă „Generează note contabile” — se creează automat din
          bonuri (2-4 rânduri per bon), apoi le poți corecta aici.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-1 pr-2 font-medium">#</th>
                <NotaHeaderEA />
                <th className="py-1 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {note.map((n) => (
                <NotaRow key={n.id} nota={n} decontId={decontId} />
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-b4l-border font-semibold">
                <td colSpan={12} className="py-1 pr-2 text-right">
                  Total
                </td>
                <td className="py-1 pr-2 text-right tabular-nums">{fmt(total)}</td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          </table>
          <p className="mt-2 text-xs text-b4l-text-muted">
            Editează direct în celule (Tab/Enter pentru salvare). Modificările se folosesc la
            exportul către ExpertAccounts.
          </p>
        </div>
      )}
    </Card>
  )
}
